import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ContractSignatoryPicker, contractSignatoryIds, defaultSignatoryIds } from "@/components/contract-signatory-picker";
import { ContractVehiclesEditor } from "@/components/contract-vehicles-editor";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import type { ContractVehicle } from "@/services/gpsdrive-client-contract.service";
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { GpsInlineLoader } from "@/components/gps-loader";
import { useQueryClient, useIsRestoring } from "@tanstack/react-query";
import {
  ChevronDown,
  Download,
  Plus,
  AlertTriangle,
  Info,
  Pencil,
  Users as UsersIcon,
  LogIn,
  Lock,
  Unlock,
  Trash2,
  Smartphone,
  CheckCircle2,
  Eye,
  EyeOff,
  User as UserIcon,
  IdCard,
  Building2,
  Camera,
  MessageCircle,
  Upload,
  FileText,
  Wallet,
  Receipt,
  Clock,
  FolderOpen,
  Copy,
  Link2,
  Monitor,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { UserVehiclesDialog } from "@/components/user-vehicles-dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/list-pagination";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserDeletionsDialog } from "@/components/user-deletions-dialog";
import { cn } from "@/lib/utils";
import {
  useUsers,
  useUsersSummary,
  useUser,
  useCreateUser,
  useUpdateUser,
  useSetUserSuspended,
  useDeleteUser,
  useDevicePermissions,
  useCompanyFleet,
  useVehicleCompanyPosition,
  useSyncUserDevicePermissions,
  useUserAvatar,
  useGpsDriveRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useChangeAccountType,
  useMyCompany,
  useCreateReseller,
  useResolveUserByTraccarId,
  useSubUsersByCreator,
  useCompanySubUserTraccarIds,
  useDeleteSubUser,
  useUserPermissions,
  useUpdateUserPermissions,
  useRestoreDefaultUserPermissions,
  useChangeUserRole,
  useClientProfile,
  useUpdateClientProfile,
  useInvoices,
  useCreateInvoice,
  useUpdateInvoice,
  useDeleteInvoice,
  useCreateSubscription,
  useClientDocuments,
  useUploadClientDocument,
  useDeleteClientDocument,
  usePlans,
  useContractTemplates,
  useContractSignatories,
  useClientContract,
  useClientContractVersions,
  useUpdateClientContract,
  useDeleteClientContract,
  useDeleteClientContractVersion,
  useStartNewClientContractVersion,
  useGenerateContractSigningLink,
  useSendSigningLinkByWhatsapp,
  useWhatsappIntegration,
  queryKeys,
} from "@/hooks/queries";
import type {
  Invoice,
  InvoiceBillingType,
  InvoiceStatus,
  SubscriptionCycle,
} from "@/services/gpsdrive-billing.service";
import type { ClientDocument, ClientDocumentCategory } from "@/services/gpsdrive-client-documents.service";
import type { UserRowSummary } from "@/services/gpsdrive-users-summary.service";
import { Car, Anchor } from "lucide-react";
import type { User } from "@/types";
import { ApiError } from "@/lib/api/client";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { exportWorkbook } from "@/lib/reports-export";
import {
  gpsDriveUsersService,
  ACCOUNT_TYPES,
  type AccountType,
  type GpsDriveUserIdentity,
} from "@/services/gpsdrive-users.service";
import type { TraccarDeviceRef } from "@/services/vehicle-permissions.service";
import { VehicleAnchorDialog } from "@/components/vehicle-anchor-dialog";
import { gpsDriveRolesService, type RoleScope } from "@/services/gpsdrive-roles.service";
import {
  gpsDriveUserPermissionsService,
  PERMISSION_CATEGORIES,
  FUNCIONARIO_CATEGORY_KEYS,
  MOTORISTA_DEFAULT_ON,
  FUNCIONARIO_DEFAULT_ON,
  MOTORISTA_PERMISSION_CATEGORIES,
  type PermissionCategoryState,
} from "@/services/gpsdrive-user-permissions.service";
import { isRestrictedAccountType } from "@/lib/account-type";
import { gpsDriveClientProfileService, type AuthorizedContact } from "@/services/gpsdrive-client-profile.service";
import {
  AuthorizedContactsEditor,
  normalizeAuthorizedContacts,
  sameAuthorizedContacts,
} from "@/components/authorized-contacts-editor";
import {
  ACCOUNT_TYPE_LABELS,
  clientProfileToCliState,
  type CliState,
  cliStateToClientProfilePayload,
} from "@/lib/user-client-profile";
import { UserPermissionsAccordion } from "@/components/user-permissions-accordion";
import { UserImportDialog } from "@/components/user-import-dialog";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { useAuth } from "@/contexts/auth-context";
import { WaitingBackend } from "@/components/waiting-backend";
import { PermissionLinkPicker } from "@/components/permission-link-picker";
import { UserActivityLogTab } from "@/components/user-activity-log-tab";
import { UserConnectedDevicesTab } from "@/components/user-connected-devices-tab";
import { SubUserFormDialog } from "@/components/sub-user-form-dialog";
import { ClientContractDocument, ClientContractPrintReport, printClientContract } from "@/components/client-contract-print-report";
import { usePermission, usePermissionGateStatus } from "@/hooks/use-permission";
import { SendLoginDialog } from "@/components/send-login-dialog";
import { AssistancePlanBadge } from "@/components/assistance-plan-badge";
import { useAssistanceVehiclePlans } from "@/hooks/queries/assistance-center";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { gpsDriveUsersSummaryService } from "@/services/gpsdrive-users-summary.service";

type DotStatus = "green" | "red" | "gray";
type YesNo = "sim" | "nao";
type AlertKind = "sem-app" | "verificar" | "ok";

// Linha de exibição da tabela. "document/subDocument/recurrence/alert/
// contract/vehicles" são conceitos de Cliente/Financeiro (fatura, CPF,
// contrato) que ainda não existem no Traccar — ficam com placeholder "—"
// nesta etapa (ver aba CLIENTE) até existir o backend próprio do GPS Drive.
// O dado real do Traccar por trás da linha fica em "user".
interface ClientRow {
  id: string;
  name: string;
  document: string;
  subDocument?: string;
  phone: string;
  recurrence: YesNo;
  alert: AlertKind;
  contract: YesNo;
  vehicles: number;
  status: DotStatus;
  user: User;
}

function toClientRow(u: User): ClientRow {
  return {
    id: String(u.id),
    name: u.name || u.email,
    document: "—",
    subDocument: undefined,
    phone: u.phone || "—",
    recurrence: "nao",
    alert: "sem-app",
    contract: "nao",
    vehicles: 0,
    status: "gray",
    user: u,
  };
}

// Ponto de partida do form "Criar grupo" (aba PERMISSÕES) — tudo desligado,
// o Administrator escolhe o que esse Grupo novo libera antes de salvar.
function blankPermissions(): PermissionCategoryState[] {
  return PERMISSION_CATEGORIES.map((def) => ({
    category: def.key,
    enabled: false,
    actions: def.actions.map((a) => ({ action: a.key, enabled: false })),
  }));
}

function motoristaDefaultPermissions(): PermissionCategoryState[] {
  return blankPermissions().map((row) => {
    const on = MOTORISTA_DEFAULT_ON[row.category];
    if (!on) return row;
    return {
      ...row,
      enabled: true,
      actions: row.actions.map((a) => ({ ...a, enabled: on.includes(a.action) })),
    };
  });
}

// Funcionário novo (2026-10-09): liga Menus do sistema + Agenda inteiros só
// nas categorias que ainda estão todas desligadas — nunca desfaz o que o
// operador (ou o Grupo de permissão escolhido) já marcou.
function withFuncionarioDefaults(rows: PermissionCategoryState[]): PermissionCategoryState[] {
  return rows.map((row) => {
    const on = FUNCIONARIO_DEFAULT_ON[row.category as keyof typeof FUNCIONARIO_DEFAULT_ON];
    if (!on || row.enabled || row.actions.some((a) => a.enabled)) return row;
    return { ...row, enabled: true, actions: row.actions.map((a) => ({ ...a, enabled: on.includes(a.action) })) };
  });
}

// Ligar a categoria já implica "Visualizar" (pedido 2026-09-11: o toggle
// separado de Visualizar, escondido dentro do accordion, confundia — o
// operador ligava só a categoria (achando que já bastava), salvava, e o
// usuário continuava sem conseguir ver nada, porque usePermission/isAllowed
// exigem categoria E ação "view" ligadas). Nunca desliga "view" ao desligar
// a categoria — categoria OFF já barra tudo sozinha (ver
// UserPermissionsAccordion), então não há necessidade de desfazer o que já
// estava marcado.
function withImpliedView(row: PermissionCategoryState, enabled: boolean): PermissionCategoryState {
  if (!enabled) return { ...row, enabled };
  return {
    ...row,
    enabled,
    actions: row.actions.map((a) => (a.action === "view" ? { ...a, enabled: true } : a)),
  };
}

// Cor da etiqueta ao lado do nome na lista de Usuários (pedido 2026-09-03).
const ACCOUNT_TYPE_BADGE_CLASS: Record<AccountType, string> = {
  admin: "bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300",
  usuario: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  funcionario: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300",
  motorista: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
  revenda: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
};

// Aba FATURAMENTO — rótulos/cores dos status reais devolvidos pelo GPS
// Drive Backend (ver InvoiceStatus em gpsdrive-billing.service.ts, já
// mapeado a partir do status bruto do Asaas).
const INVOICE_STATUS_STYLE: Record<InvoiceStatus, { label: string; row: string; badge: string }> = {
  pendente: {
    label: "Aguardando pagamento",
    row: "bg-amber-50 dark:bg-amber-950/20",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200",
  },
  paga: {
    label: "Paga",
    row: "bg-emerald-50/50 dark:bg-emerald-950/10",
    badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  },
  vencida: {
    label: "Vencida",
    row: "bg-red-50 dark:bg-red-950/20",
    badge: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200",
  },
  estornada: {
    label: "Estornada",
    row: "bg-muted/40",
    badge: "bg-muted text-muted-foreground",
  },
  outro: {
    label: "Outro",
    row: "",
    badge: "bg-muted text-muted-foreground",
  },
};

// Selo "Avulsa"/"Recorrência" na aba FATURAMENTO — vem de
// InvoiceDto.origin (backend deriva de AsaasPayment.subscription: presente
// = gerada por uma assinatura, ausente = cobrança avulsa de verdade).
const INVOICE_ORIGIN_LABEL: Record<Invoice["origin"], { label: string; badge: string }> = {
  avulsa: { label: "Avulsa", badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  recorrencia: { label: "Recorrência", badge: "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300" },
};

// Preço exibido no seletor de plano por veículo (Nova Recorrência) — ver
// admin.plans.tsx para o mesmo formatador.
const planCurrencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const CYCLE_LABEL: Record<SubscriptionCycle, string> = {
  WEEKLY: "Semanal",
  BIWEEKLY: "Quinzenal",
  MONTHLY: "Mensal",
  BIMONTHLY: "Bimestral",
  QUARTERLY: "Trimestral",
  SEMIANNUALLY: "Semestral",
  YEARLY: "Anual",
};

const BILLING_TYPE_LABEL: Record<string, string> = {
  PIX: "PIX",
  BOLETO: "Boleto",
  CREDIT_CARD: "Cartão de crédito",
  UNDEFINED: "Cliente escolhe",
  TRANSFER: "Transferência",
  DEPOSIT: "Depósito",
};

// DOCUMENTOS — rótulos das categorias fechadas do backend (ver
// CLIENT_DOCUMENT_CATEGORIES em client-documents.dto.ts, gpsdrive-backend).
const DOCUMENT_CATEGORY_LABEL: Record<ClientDocumentCategory, string> = {
  contrato: "Contrato",
  documento_pessoal: "Documento pessoal (RG/CNH)",
  comprovante_residencia: "Comprovante de residência",
  comprovante_renda: "Comprovante de renda",
  outro: "Outro",
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Telefone/Celular só número (pedido 2026-09-01: "o celular ele ta
// ficando o e-mail, então tem que mudar para aceitar só números no popup
// de criar e editar usuário") — sem type="tel"/autoComplete, o navegador
// às vezes autocompletava o campo com o e-mail salvo por engano (campo
// vizinho do Email/Login). 15 dígitos cobre até código de país + DDD +
// número, mais que suficiente pra Brasil.
function onlyDigits(value: string, maxLen = 15): string {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

function UsersPage() {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState("todos");
  const [status, setStatus] = useState("todos");
  const [recorrencia, setRecorrencia] = useState("recorrente");
  const [contrato, setContrato] = useState("contratado");
  const [filtro, setFiltro] = useState("");
  const [imei, setImei] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Fonte real de dados: GET /api/users (Traccar). Sem mock, sem fallback.
  const { data: usersData, isLoading, isError, error } = useUsers();
  // isRestoring (PersistQueryClientProvider, __root.tsx): true só durante a
  // janela — sempre presente, mesmo com cache local, e tipicamente rápida
  // (leitura síncrona do localStorage) — entre o primeiro render desta
  // página e o momento em que o cache salvo em localStorage termina de ser
  // reidratado no QueryClient. Bug real encontrado 2026-09-10: enquanto
  // isRestoring===true, o próprio React Query FORÇA fetchStatus:"idle" em
  // toda query (ver _optimisticResults==="isRestoring" em
  // queryObserver.js), então `isLoading` fica FALSE mesmo sem nenhum dado
  // ainda — sem este gate extra, esse instante entre "página montou" e
  // "cache reidratado" passava direto pelo `isLoading || isError` de baixo
  // como se já tivesse terminado de carregar, e as linhas (UserRow, mesma
  // lógica) mostravam os skeletons cinza baseados em `isFetched` (que
  // também começa false até a reidratação aplicar os dados salvos) — esse
  // é o "aparece com skeleton e só depois preenche" relatado, mesmo em
  // sessões que já tinham visitado esta tela antes (cache real salvo, só
  // ainda não aplicado no instante do primeiro render).
  const isRestoring = useIsRestoring();
  // Pedido 2026-09-11: "o admin não precisa ver na tela de usuário o
  // usuário que o cliente criou" — sub-usuários só aparecem dentro da aba
  // USUÁRIOS de Editar Usuário do respectivo Cliente, nunca nesta lista
  // principal (que lista direto do Traccar, sem noção de createdByUserId).
  // gpsDriveReady evita a corrida de boot (F5) — mesmo critério usado no
  // resto do arquivo (ver UserRow#gpsDriveReady).
  const { gpsDriveStatus } = useAuth();
  const gpsDriveReady = gpsDriveStatus !== "loading";
  const { data: subUserTraccarIds } = useCompanySubUserTraccarIds(gpsDriveReady);
  const subUserTraccarIdSet = useMemo(() => new Set(subUserTraccarIds ?? []), [subUserTraccarIds]);
  const deleteUser = useDeleteUser();
  const updateUser = useUpdateUser();
  const setUserSuspended = useSetUserSuspended();
  // Bug relatado 2026-09-01: usuário sem NENHUMA permissão ativa (nem
  // "users") ainda conseguia abrir "Editar Usuário" pelo lápis — só as
  // AÇÕES de dentro do dialog (bloquear/excluir/logar como) já eram
  // bloqueadas por permissão (ver canEditUsers/canDeleteUsers/
  // canImpersonate em ClientDetails), a abertura em si nunca foi. Mesmo
  // critério usado lá: categoria "users" desligada já derruba isto sozinha
  // (ver usePermission).
  const canOpenEditUser = usePermission("users", "edit");

  // Todas as linhas visíveis (sem o filtro de texto ainda aplicado) — base
  // para o resumo em lote abaixo. Precisa ser separado de `clients`
  // (filtrado) porque digitar no campo "Filtrar..." NUNCA deve disparar um
  // novo request de resumo: são as mesmas linhas, só menos exibidas.
  const allClients = useMemo(
    () => (usersData ?? []).filter((u) => !subUserTraccarIdSet.has(u.id)).map(toClientRow),
    [usersData, subUserTraccarIdSet],
  );

  const clients = useMemo(() => {
    const needle = filtro.trim().toLowerCase();
    if (!needle) return allClients;
    return allClients.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.user.email.toLowerCase().includes(needle) ||
        c.phone.includes(needle),
    );
  }, [allClients, filtro]);

  const total = clients.length;

  // Paginação (pedido 2026-09-30: com ~3.400 clientes a tela demorava pra
  // abrir porque montava todas as linhas + pedia o resumo de todos de uma
  // vez). Agora só a página aberta é renderizada e só ela busca resumo.
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    setPage(1);
  }, [filtro, pageSize]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const pageClients = useMemo(
    () => clients.slice((page - 1) * pageSize, page * pageSize),
    [clients, page, pageSize],
  );

  // Resumo em lote (avatar/Grupo/CPF-CNPJ/celular/veículos/recorrência)
  // pra tabela inteira, numa chamada só — ver useUsersSummary. Antes, cada
  // UserRow disparava esses mesmos dados sozinha (5-7 requests por linha,
  // ver diagnóstico de lentidão 2026-09-11). `enabled` combina o mesmo
  // gate de boot (gpsDriveReady) usado no resto do arquivo com
  // `!isRestoring` (mesmo motivo do gate em `usersData` acima: durante a
  // reidratação do cache local, isLoading force-fica false mesmo sem
  // nenhum dado ainda).
  const traccarUserIds = useMemo(() => pageClients.map((c) => c.user.id), [pageClients]);
  const { data: summaryRows, isFetched: summaryFetched } = useUsersSummary(
    traccarUserIds,
    gpsDriveReady && !isRestoring,
  );
  const summaryReady = !isRestoring && summaryFetched;
  const summaryById = useMemo(
    () => new Map((summaryRows ?? []).map((r) => [r.id, r])),
    [summaryRows],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientRow | null>(null);
  const [editingInitialTab, setEditingInitialTab] = useState<string>("principal");
  const [vehiclesClient, setVehiclesClient] = useState<ClientRow | null>(null);
  // "Situação" (coluna da tabela, pedido 2026-09-12, revisado no mesmo dia:
  // bug relatado — suspender um cliente bloqueava o login inteiro no
  // Traccar, o cliente nem chegava a ver o aviso de pendência, só um erro
  // genérico de login). SOFT desde então: nunca mais toca no `disabled` do
  // Traccar (esse continua existindo só como o toggle legado Bloquear/
  // Desbloquear de dentro de Editar Usuário, ver onToggleDisabled abaixo,
  // deliberadamente separado) — Suspender/Reativar aqui só marca
  // User.suspended no GPS Drive Backend (ver gpsDriveUsersService.
  // setSuspended), que é o que GET /users/me/billing-status lê pra
  // decidir o que o app do cliente mostra. Reativar um usuário que ainda
  // deve mostra este dialog pedindo quantos dias de "acesso de confiança"
  // conceder (ver handleToggleSituacao abaixo).
  const [trustAccessTarget, setTrustAccessTarget] = useState<{
    client: ClientRow;
    identityId: string;
  } | null>(null);
  const [checkingSituacaoId, setCheckingSituacaoId] = useState<string | null>(null);

  const handleToggleSituacao = async (client: ClientRow, nextDisabled: boolean) => {
    setCheckingSituacaoId(client.id);
    let identity: GpsDriveUserIdentity;
    try {
      identity = await gpsDriveUsersService.resolveByTraccarId(client.user.id);
    } catch (err) {
      setCheckingSituacaoId(null);
      toast.error(nextDisabled ? "Não foi possível suspender" : "Não foi possível reativar", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
      return;
    }

    if (nextDisabled) {
      setCheckingSituacaoId(null);
      setUserSuspended.mutate(
        { userId: identity.id, suspended: true },
        {
          onSuccess: () => toast.success("Usuário suspenso", { description: client.name }),
          onError: (err) =>
            toast.error("Não foi possível suspender", {
              description: err instanceof GpsDriveApiError ? err.message : undefined,
            }),
        },
      );
      return;
    }

    // Reativar: confere primeiro se o cliente ainda deve, pra decidir se
    // mostra o aviso de "acesso de confiança" — nunca bloqueia o botão por
    // uma falha nessa checagem (Asaas desligado/fora do ar), só segue
    // direto pra reativação normal nesse caso.
    try {
      const { hasOverdueInvoice } = await gpsDriveUsersService.overdueStatus(identity.id);
      if (hasOverdueInvoice) {
        setTrustAccessTarget({ client, identityId: identity.id });
        return;
      }
    } catch {
      // segue pra reativação direta abaixo
    } finally {
      setCheckingSituacaoId(null);
    }

    setUserSuspended.mutate(
      { userId: identity.id, suspended: false },
      {
        onSuccess: () => toast.success("Usuário reativado", { description: client.name }),
        onError: (err) =>
          toast.error("Não foi possível reativar", {
            description: err instanceof GpsDriveApiError ? err.message : undefined,
          }),
      },
    );
  };
  // "Contratos" (painel expandido) abre um popup próprio de ver/baixar —
  // pedido 2026-09-11: nunca mais a aba CONTRATO do dialog de Editar
  // Usuário inteiro, só pra consultar/baixar o contrato já preenchido.
  const [contractPreviewClient, setContractPreviewClient] = useState<ClientRow | null>(null);

  // Exporta a lista atualmente filtrada (mesma busca em `clients`) — usa o
  // resumo em lote já carregado (summaryById) pra trazer Celular/CPF-CNPJ/
  // Grupo sem disparar nenhum request novo.
  const handleExport = async () => {
    // Resumo só existe pra página aberta — busca o dos filtrados na hora.
    let exportSummary = summaryById;
    try {
      const rows = await gpsDriveUsersSummaryService.summary(clients.map((c) => c.user.id));
      exportSummary = new Map(rows.map((r) => [r.id, r]));
    } catch {
      toast.error("Não foi possível carregar CPF/celular de todos — exportando só o básico");
    }
    const headerRow = ["Nome", "Email", "Telefone", "Celular", "CPF/CNPJ", "Grupo do usuário", "Veículos"];
    const dataRows = clients.map((c) => {
      const summary = exportSummary.get(c.user.id);
      return [
        c.name,
        c.user.email,
        c.user.phone || "",
        summary?.phone || "",
        summary?.document || "",
        ACCOUNT_TYPE_LABELS[summary?.accountType ?? "usuario"],
        summary?.vehicleCount ?? 0,
      ];
    });
    exportWorkbook(
      [{ name: "Usuários", rows: [headerRow, ...dataRows] }],
      `usuarios-${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  return (
    <div className="min-h-full bg-muted/40 space-y-4">
      {/* Painel expandido (seta da linha, ver ClientDetails) depende do GPS
          Drive Backend (identity/faturas/contrato/documentos) — sem este
          banner, uma sessão de gestão expirada (auth-context.tsx#
          gpsDriveStatus) deixava o painel preso mostrando "Essas
          informações estarão disponíveis futuramente" pra sempre, sem
          nenhum jeito de o Administrator perceber o motivo (o único banner
          existente nesta tela ficava dentro do dialog Editar Usuário,
          nunca visível na lista em si — bug real 2026-08-28). Mesmo
          componente/padrão de routes/index.tsx e dos dialogs desta
          página. */}
      <GpsDriveSessionBanner />
      <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-background px-4 py-3">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <UsersIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xl font-bold leading-none">Usuários</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {total.toLocaleString("pt-BR")} usuários
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
            <Download className="h-4 w-4" />
            Exportar
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" />
            Importar
          </Button>
          <UserDeletionsDialog />
          <Button size="icon" className="h-9 w-9 rounded-full" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 rounded-full bg-amber-400/90 text-white hover:bg-amber-500"
          >
            <AlertTriangle className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-background p-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <FilterSelect
            label="Tipo de usuário"
            value={tipo}
            onChange={setTipo}
            options={[
              { value: "todos", label: "Todos" },
              { value: "cliente", label: "Cliente" },
              { value: "revenda", label: "Revenda" },
              { value: "admin", label: "Administrador" },
            ]}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "todos", label: "Todos" },
              { value: "ativo", label: "Ativo" },
              { value: "inativo", label: "Inativo" },
            ]}
          />
          <FilterSelect
            label="Recorrência"
            value={recorrencia}
            onChange={setRecorrencia}
            options={[
              { value: "recorrente", label: "Recorrente" },
              { value: "nao", label: "Não recorrente" },
            ]}
          />
          <FilterSelect
            label="Contrato"
            value={contrato}
            onChange={setContrato}
            options={[
              { value: "contratado", label: "Contratado" },
              { value: "sem", label: "Sem contrato" },
            ]}
          />
          <div className="col-span-2 md:col-span-1">
            <Input
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Filtrar..."
              className="h-9 rounded-full"
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Input
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="Buscar por IMEI"
              className="h-9 rounded-full"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {(isLoading || isRestoring) || isError ? (
        <WaitingBackend error={error} />
      ) : (
      <div className="overflow-x-auto rounded-xl border border-border bg-background">
        <div className="min-w-[860px]">
        <div className="grid grid-cols-[32px_32px_minmax(0,1fr)_100px_130px_96px_84px_76px_40px] xl:grid-cols-[32px_32px_minmax(0,2.5fr)_minmax(130px,1fr)_100px_minmax(120px,1fr)_96px_84px_76px_40px] items-center gap-3 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <div />
          <div />
          <div>Nome do usuário</div>
          <div className="hidden xl:block">CPF/CNPJ</div>
          <div>Situação</div>
          <div>Celular</div>
          <div className="text-center">Recorrência</div>
          <div className="text-center">Contrato</div>
          <div className="text-center">Veículos</div>
          <div />
        </div>

        {clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Nenhum usuário encontrado.
          </div>
        ) : (
        <div className="divide-y divide-border">
          {pageClients.map((c) => {
            const isOpen = expandedId === c.id;
            return (
            <div key={c.id}>
            <UserRow
              client={c}
              summary={summaryById.get(c.user.id)}
              summaryReady={summaryReady}
              isOpen={isOpen}
              canOpenEditUser={canOpenEditUser}
              checkingSituacao={checkingSituacaoId === c.id}
              onToggle={() => setExpandedId(isOpen ? null : c.id)}
              onOpenVehicles={() => setVehiclesClient(c)}
              onEdit={() => { setEditingInitialTab("principal"); setEditingClient(c); }}
              onToggleSituacao={(nextDisabled) => handleToggleSituacao(c, nextDisabled)}
            />
            {isOpen && (
              <ClientDetails
                client={c}
                onOpenTab={(tab) => {
                  setEditingInitialTab(tab);
                  setEditingClient(c);
                }}
                onOpenContract={() => setContractPreviewClient(c)}
                onDelete={async (reason) => {
                  await deleteUser.mutateAsync({ id: c.user.id, email: c.user.email, reason });
                  toast.success("Usuário excluído", { description: `${c.name} foi removido.` });
                }}
                onToggleDisabled={(disabled) => {
                  updateUser.mutate(
                    { id: c.user.id, data: { ...c.user, disabled } },
                    {
                      onSuccess: () => toast.success(disabled ? "Usuário desabilitado" : "Usuário habilitado", { description: c.name }),
                      onError: (err) => toast.error("Não foi possível atualizar", { description: err instanceof ApiError ? err.message : undefined }),
                    },
                  );
                }}
              />
            )}
            </div>
            );
          })}
        </div>
        )}
        {total > 0 && (
          <ListPagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            total={total}
            onPage={(p) => {
              setExpandedId(null);
              setPage(p);
            }}
            onPageSize={setPageSize}
          />
        )}
        </div>
      </div>
      )}
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <UserImportDialog open={importOpen} onOpenChange={setImportOpen} existingUsers={usersData ?? []} />
      {/* key={editingClient?.user.id}: sem isso, o dialog é UMA instância só
          reaproveitada pra qualquer usuário (initial/open só trocam props,
          nunca desmontam o componente) — todo o estado local (nome, email,
          telefone, aba Cliente/Financeiro etc.) ficava vivo entre uma edição
          e outra. Editar o usuário A e depois B mostrava os dados de A por
          um instante (até o fetch fresco de B terminar e sobrescrever)
          antes de "corrigir sozinho" — bug relatado 2026-08-28. Trocar a
          key força o React a desmontar/remontar o dialog inteiro a cada
          usuário diferente, então ele já nasce em branco/carregando, nunca
          com dado errado de outra pessoa. */}
      <CreateUserDialog
        key={editingClient?.user.id ?? "edit-dialog"}
        mode="edit"
        initial={editingClient}
        initialTab={editingInitialTab}
        open={!!editingClient}
        onOpenChange={(o) => !o && setEditingClient(null)}
      />
      <UserVehiclesDialog
        client={vehiclesClient ? { userId: vehiclesClient.user.id, name: vehiclesClient.name } : null}
        onClose={() => setVehiclesClient(null)}
      />
      <ContractPreviewDialog
        client={contractPreviewClient}
        onOpenChange={(o) => !o && setContractPreviewClient(null)}
      />
      <TrustAccessDialog
        target={trustAccessTarget}
        onOpenChange={(o) => !o && setTrustAccessTarget(null)}
        onGranted={() => {
          setTrustAccessTarget(null);
          void queryClient.invalidateQueries({ queryKey: queryKeys.users });
        }}
      />
    </div>
  );
}

// "Acesso de confiança" (coluna Situação, pedido 2026-09-12) — mostrado só
// quando o Administrator tenta reativar um usuário que ainda tem fatura
// vencida no Asaas (ver UsersPage#handleToggleSituacao). O prazo escolhido
// aqui vira trustAccessUntil no backend; TrustAccessEnforcer rebloqueia
// sozinho se o prazo passar sem o cliente pagar.
function TrustAccessDialog({
  target,
  onOpenChange,
  onGranted,
}: {
  target: { client: ClientRow; identityId: string } | null;
  onOpenChange: (open: boolean) => void;
  onGranted: () => void;
}) {
  const [days, setDays] = useState(7);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) setDays(7);
  }, [target]);

  const handleConfirm = async () => {
    if (!target) return;
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      toast.error("Informe um número de dias entre 1 e 90");
      return;
    }
    setSaving(true);
    try {
      await gpsDriveUsersService.grantTrustAccess(target.identityId, days);
      toast.success("Acesso de confiança concedido", {
        description: `${target.client.name} pode acessar por ${days} ${days === 1 ? "dia" : "dias"} — se não pagar até lá, é bloqueado de novo automaticamente.`,
      });
      onGranted();
    } catch (err) {
      toast.error("Não foi possível conceder o acesso", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Cliente ainda deve
          </DialogTitle>
          <DialogDescription>
            {target?.client.name} tem fatura vencida no Asaas. Deseja liberar o acesso mesmo assim,
            por quantos dias? Se não pagar até o prazo, o sistema bloqueia de novo sozinho.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="trust-access-days">Dias de acesso de confiança</Label>
          <Input
            id="trust-access-days"
            type="number"
            min={1}
            max={90}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            disabled={saving}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Liberando…" : "Liberar acesso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Placeholder de uma célula da linha enquanto rowDetailsReady === false
// (pedido 2026-09-03: revelar a linha inteira de uma vez, não campo a
// campo — ver rowDetailsReady em UserRow).
// Etiqueta "último acesso" ao lado do Admin/Usuário/Funcionário (pedido
// 2026-09-29). Cor pela recência; detalhes (data exata, aparelho, último
// login, acessos em 30 dias) no tooltip.
const DEVICE_LABEL = { android: "App Android", ios: "App iPhone", web: "Navegador" } as const;
const dateTimeFmt = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" });

function relativeSince(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 20) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return "ontem";
  if (d < 30) return `há ${d} dias`;
  const m = Math.floor(d / 30);
  if (m < 12) return `há ${m} ${m === 1 ? "mês" : "meses"}`;
  const y = Math.floor(d / 365);
  return `há ${y} ${y === 1 ? "ano" : "anos"}`;
}

function LastSeenBadge({ summary }: { summary: UserRowSummary }) {
  const { lastSeenAt, lastSeenDevice, lastLoginAt, logins30d = 0 } = summary;
  if (!lastSeenAt) {
    return (
      <span
        className="shrink-0 rounded-full border border-dashed border-slate-300 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-wide text-slate-500 dark:border-slate-600 dark:text-slate-400"
        title="Este usuário ainda não entrou no sistema nem no app."
      >
        Nunca acessou
      </span>
    );
  }
  const ageH = (Date.now() - new Date(lastSeenAt).getTime()) / 3_600_000;
  const tone =
    ageH < 24
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
      : ageH < 24 * 7
        ? "bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300"
        : ageH < 24 * 30
          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400";
  const DeviceIcon = lastSeenDevice === "web" ? Monitor : lastSeenDevice ? Smartphone : Clock;
  const rel = relativeSince(lastSeenAt);
  const title = [
    `Último acesso: ${dateTimeFmt.format(new Date(lastSeenAt))}`,
    lastSeenDevice ? `Pelo: ${DEVICE_LABEL[lastSeenDevice]}` : null,
    lastLoginAt ? `Último login com senha: ${dateTimeFmt.format(new Date(lastLoginAt))}` : null,
    `Logins nos últimos 30 dias: ${logins30d}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-wide", tone)} title={title}>
      <DeviceIcon className="h-2.5 w-2.5" />
      {rel === "agora" ? "Online agora" : `Acessou ${rel}`}
    </span>
  );
}

function RowFieldSkeleton({ className }: { className?: string }) {
  return <span className={cn("inline-block h-3 w-12 animate-pulse rounded bg-muted", className)} />;
}

// Linha da tabela — recebe os dados reais (avatar, CPF/CNPJ + celular do
// Cliente/Financeiro, veículos vinculados, recorrência) já resolvidos em
// lote pelo pai (ver UsersPage#useUsersSummary), em vez de buscar cada um
// sozinha. Antes, `toClientRow` só mapeava o User cru do Traccar e a linha
// inteira ficava com "—" fixo pra tudo isso (pedido 2026-09-01: "não
// aparece a imagem do usuário só se eu entrar em editar, e o cpf/cnpj não
// aparece... recorrência o valor da recorrência"); a correção original
// (2026-09-03) tinha cada linha disparando 5-7 requests próprios
// (useUserAvatar/useDevicePermissions/useResolveUserByTraccarId/
// useClientProfile/useInvoices + 2 de "aquecimento" de cache) — pra N
// usuários isso virava N×7 requests, e a resolução de identidade por
// linha REFAZIA a busca da lista inteira de usuários do Traccar a cada
// linha (diagnóstico de lentidão 2026-09-11). `summary` undefined
// enquanto o resumo em lote não chegou é tratado igual a "—" (mesmo
// significado de antes).
function UserRow({
  client: c,
  summary,
  summaryReady,
  isOpen,
  canOpenEditUser,
  checkingSituacao,
  onToggle,
  onOpenVehicles,
  onEdit,
  onToggleSituacao,
}: {
  client: ClientRow;
  summary: UserRowSummary | undefined;
  summaryReady: boolean;
  isOpen: boolean;
  canOpenEditUser: boolean;
  checkingSituacao: boolean;
  onToggle: () => void;
  onOpenVehicles: () => void;
  onEdit: () => void;
  onToggleSituacao: (nextDisabled: boolean) => void;
}) {
  const avatarUrl = summary?.avatarUrl ? resolveGpsDriveUrl(summary.avatarUrl) : undefined;
  const recorrenciaLabel =
    summary?.recurrenceValue != null ? planCurrencyFormatter.format(summary.recurrenceValue) : "—";
  const document = summary?.document || "—";
  // Celular do Cliente/Financeiro é o campo de verdade pra esta coluna
  // (rótulo "Celular"); Telefone do Traccar (aba Principal) só como
  // reserva pra quem preencheu um e não o outro.
  const phone = summary?.phone || c.phone;
  const vehicleCount = summary?.vehicleCount ?? 0;
  // Linha inteira revela junto, de uma vez, em vez de cada campo "piscar"
  // isolado — mesmo espírito do rowDetailsReady original (pedido
  // 2026-09-03), só que agora é UMA query só (useUsersSummary) que
  // resolve pra tabela inteira, não mais 5 por linha.
  const rowDetailsReady = summaryReady;

  // Situação (Suspender/Reativar) — vem do resumo em lote agora (soft,
  // GPS Drive Backend), nunca mais de `c.user.disabled` do Traccar (ver
  // comentário em UsersPage#handleToggleSituacao). `false` enquanto
  // summary não chega ainda é só o valor de partida do botão — a label
  // real só aparece com rowDetailsReady, mesmo tratamento das outras
  // colunas desta linha.
  const disabled = summary?.suspended ?? false;

  return (
    <div className="grid grid-cols-[32px_32px_minmax(0,1fr)_100px_130px_96px_84px_76px_40px] xl:grid-cols-[32px_32px_minmax(0,2.5fr)_minmax(130px,1fr)_100px_minmax(120px,1fr)_96px_84px_76px_40px] items-center gap-3 px-3 py-2.5 hover:bg-muted/30">
      <button
        onClick={onToggle}
        aria-label={isOpen ? "Recolher" : "Expandir"}
        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted"
      >
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </button>
      <div className="relative">
        <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-semibold uppercase text-muted-foreground">
          {!rowDetailsReady ? (
            <span className="h-full w-full animate-pulse rounded-full bg-muted-foreground/20" />
          ) : avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            c.name.trim()[0]
          )}
        </div>
        <span
          className={cn(
            "absolute -left-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background",
            c.status === "green" && "bg-emerald-500",
            c.status === "red" && "bg-red-500",
            c.status === "gray" && "bg-muted-foreground/40",
          )}
        />
      </div>
      <div className="min-w-0">
        <div title={c.name} className="line-clamp-2 break-words text-sm font-semibold uppercase leading-tight text-foreground">
          {c.name}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            {rowDetailsReady ? document : <RowFieldSkeleton />}
            <Info className="h-3 w-3" />
          </span>
          {/* Etiqueta Admin/Usuário/Funcionário ao lado do nome (pedido
              2026-09-03) — accountType já vem de graça do resumo em lote
              buscado pelo pai pra outros campos desta linha, zero fetch
              novo. */}
          {rowDetailsReady && summary?.accountType && (
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-wide",
                ACCOUNT_TYPE_BADGE_CLASS[summary.accountType],
              )}
            >
              {ACCOUNT_TYPE_LABELS[summary.accountType]}
            </span>
          )}
          {rowDetailsReady && summary && summary.lastSeenAt !== undefined && <LastSeenBadge summary={summary} />}
        </div>
      </div>
      <div className="hidden min-w-0 space-y-1 xl:block">
        <div className="truncate text-sm text-muted-foreground">
          {rowDetailsReady ? document : <RowFieldSkeleton />}
        </div>
      </div>
      <div className="flex justify-start">
        <button
          type="button"
          disabled={!canOpenEditUser || checkingSituacao || !rowDetailsReady}
          title={!canOpenEditUser ? "Sem permissão" : disabled ? "Reativar usuário" : "Suspender usuário"}
          onClick={() => onToggleSituacao(!disabled)}
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
            disabled
              ? "bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
              : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400",
          )}
        >
          {checkingSituacao ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <span className={cn("h-1.5 w-1.5 rounded-full", disabled ? "bg-rose-500" : "bg-emerald-500")} />
          )}
          {rowDetailsReady ? (disabled ? "Suspenso" : "Ativo") : "—"}
        </button>
      </div>
      <div className="truncate text-sm text-foreground">
        {rowDetailsReady ? phone : <RowFieldSkeleton />}
      </div>
      <div className="flex justify-center text-sm text-muted-foreground">
        {rowDetailsReady ? recorrenciaLabel : <RowFieldSkeleton />}
      </div>
      <div className="flex justify-center text-sm text-muted-foreground">—</div>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onOpenVehicles}
          className="inline-flex min-w-[56px] items-center justify-center rounded-full border border-border bg-background px-3 py-0.5 text-xs tabular-nums text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-primary"
          aria-label="Ver veículos vinculados"
        >
          {rowDetailsReady ? vehicleCount : <RowFieldSkeleton className="w-4" />}
        </button>
      </div>
      <div className="flex shrink-0 justify-end">
        <Button
          size="icon"
          disabled={!canOpenEditUser}
          title={!canOpenEditUser ? "Sem permissão" : undefined}
          onClick={onEdit}
          className="h-8 w-8 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Painel expandido (seta da linha) — reorganizado em 3 cards
// (Financeiro/Gestão/Controle) 2026-08-28. Só monta quando a linha está
// aberta (ver isOpen && <ClientDetails/> acima), então as queries daqui
// nunca disparam pras linhas fechadas. resolveByTraccarId pode dar 403
// pra quem não é Administrator/Manager — nesse caso identityError fica
// true e o Card 1 mostra o estado vazio em vez de "Carregando…" pra
// sempre. Faturas/Contrato/Documentos são os MESMOS dados reais das abas
// FATURAMENTO/CONTRATO/DOCUMENTOS do Editar Usuário — nada inventado
// aqui, só resumidos. "Último/Próximo pagamento" olham só a 1ª página de
// faturas (12 mais recentes) — suficiente pro resumo, sem sobrecarregar
// a lista com uma busca completa só pra isso.
function ClientDetails({
  client,
  onDelete,
  onToggleDisabled,
  onOpenTab,
  onOpenContract,
}: {
  client: ClientRow;
  // Rejeita se a exclusão falhar — o erro aparece dentro do próprio popup.
  onDelete: (reason: string) => Promise<void>;
  onToggleDisabled: (disabled: boolean) => void;
  onOpenTab: (tab: string) => void;
  onOpenContract: () => void;
}) {
  // gpsDriveReady: mesmo critério de UserRow acima — evita disparar com
  // token do GPS Drive Backend ainda vazio logo após F5.
  const { gpsDriveStatus } = useAuth();
  const gpsDriveReady = gpsDriveStatus !== "loading";
  // Mesmo gate de UsersPage/UserRow#isRestoring acima — este painel só
  // monta quando o Administrator expande uma linha (interação depois do
  // mount da página), então na prática isRestoring quase sempre já é
  // false a essa altura; mantido por consistência/segurança, não porque
  // foi observado piscando aqui.
  const isRestoring = useIsRestoring();
  const {
    data: identity,
    isLoading: loadingIdentity,
    isError: identityError,
  } = useResolveUserByTraccarId(client.user.id, gpsDriveReady);
  const { data: invoicesPage, isLoading: loadingInvoices } = useInvoices(identity?.id, 0);
  const { data: contract } = useClientContract(identity?.id);
  const { data: documents } = useClientDocuments(identity?.id);

  const canEditUsers = usePermission("users", "edit");
  const canDeleteUsers = usePermission("users", "delete");
  const canImpersonate = usePermission("users", "impersonate");

  // "Logar como" (impersonar) e Excluir pedem confirmação num AlertDialog
  // (não mais window.confirm()) — ver auditoria 2026-08-28. impersonate()
  // troca só a sessão do GPS Drive Backend (perfil/permissões/faturamento
  // etc.) pela do usuário alvo — o mapa/rastreamento (Traccar) continua
  // sendo o do administrador, ver comentário em AuthProvider#impersonate.
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [confirmLoginAsOpen, setConfirmLoginAsOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Motivo obrigatório (pedido 2026-09-30) — fica gravado em "Excluídos".
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteReasonValid = deleteReason.trim().length >= 3;
  const handleDeleteConfirm = async () => {
    if (!deleteReasonValid || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(deleteReason.trim());
      setConfirmDeleteOpen(false);
    } catch (err) {
      setDeleteError(
        err instanceof Error && err.message ? err.message : "Não foi possível excluir. Tente de novo.",
      );
    } finally {
      setDeleting(false);
    }
  };
  const [sendLoginOpen, setSendLoginOpen] = useState(false);
  const [impersonating, setImpersonatingLoading] = useState(false);
  const handleLoginAsConfirm = async () => {
    if (!identity) return;
    setImpersonatingLoading(true);
    try {
      await impersonate(identity.id);
      setConfirmLoginAsOpen(false);
      toast.success(`Logado como ${client.name}`);
      void navigate({ to: "/" });
    } catch (err) {
      toast.error("Não foi possível logar como este usuário", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setImpersonatingLoading(false);
    }
  };

  const invoices = invoicesPage?.invoices ?? [];
  const lastPaid = [...invoices]
    .filter((i) => i.status === "paga")
    .sort((a, b) => (b.paymentDate ?? b.dueDate).localeCompare(a.paymentDate ?? a.dueDate))[0];
  const nextDue = [...invoices]
    .filter((i) => i.status === "pendente" || i.status === "vencida")
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
  const hasOverdue = invoices.some((i) => i.status === "vencida");
  const accountStatusLabel = hasOverdue ? "Em atraso" : nextDue ? "Em dia" : invoices.length ? "Sem pendências" : undefined;

  // Duas causas bem diferentes pra "sem financeiro pra mostrar" — misturar
  // as duas atrás da mesma mensagem genérica ("estará disponível
  // futuramente") confundia quem via um cliente real, com a aba
  // FATURAMENTO já implementada, mas nunca sincronizado com o Asaas (bug
  // relatado 2026-08-28: "mas a gente já implementou"). Distinguindo:
  // identityError = problema de sessão/permissão pra resolver ESTE
  // usuário (nada a ver com o cliente em si); clientNotSynced = a
  // funcionalidade existe e está ligada, só falta cadastrar/sincronizar
  // este cliente específico (aba CLIENTE, Editar Usuário).
  const clientNotSynced = !identityError && invoicesPage !== undefined && invoicesPage.asaasCustomerId === null;
  const loadingFinance = !identityError && (isRestoring || loadingIdentity || (!!identity && loadingInvoices));

  return (
    <div className="space-y-3 border-t border-border bg-muted/30 p-3">
      {/* Card 1 — Informações Financeiras */}
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Wallet className="h-4 w-4" /> Informações Financeiras
        </div>
        {identityError ? (
          <div className="flex flex-col items-center justify-center gap-1.5 py-2 text-center text-muted-foreground">
            <Building2 className="h-5 w-5" />
            <div className="text-xs">
              Não foi possível carregar — verifique sua sessão/permissão e tente de novo.
            </div>
          </div>
        ) : !loadingFinance && invoices.length > 0 ? (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <FinanceStat
              icon={<Receipt className="h-4 w-4" />}
              label="Último pagamento"
              value={lastPaid ? planCurrencyFormatter.format(lastPaid.value) : "—"}
              detail={
                lastPaid
                  ? new Date(`${lastPaid.paymentDate ?? lastPaid.dueDate}T00:00:00`).toLocaleDateString("pt-BR")
                  : "Sem pagamentos ainda"
              }
            />
            <FinanceStat
              icon={<Clock className="h-4 w-4" />}
              label="Próximo pagamento"
              value={nextDue ? planCurrencyFormatter.format(nextDue.value) : "—"}
              detail={
                nextDue
                  ? `Vence em ${new Date(`${nextDue.dueDate}T00:00:00`).toLocaleDateString("pt-BR")}`
                  : "Nada pendente"
              }
            />
            <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-muted/40 p-2.5 text-center">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Status da conta
              </span>
              {accountStatusLabel ? (
                <span
                  className={cn(
                    "inline-flex rounded px-2 py-0.5 text-xs font-semibold",
                    hasOverdue
                      ? "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200"
                      : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
                  )}
                >
                  {accountStatusLabel}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </div>
          </div>
        ) : !loadingFinance && !clientNotSynced ? (
          <div className="py-2 text-center text-xs text-muted-foreground">Nenhuma cobrança registrada ainda.</div>
        ) : (
          // Cobre tanto "ainda carregando" quanto "carregou e confirmou
          // não sincronizado" com a MESMA mensagem — de propósito (bug
          // relatado 2026-08-28: usuário queria a frase direto, sem
          // "Carregando…" piscando no meio antes dela). A grande maioria
          // dos clientes reais ainda não tem o financeiro configurado, "não
          // sincronizado" é o palpite otimista certo quase sempre; se os
          // dados carregarem e for outra coisa (faturas reais, ou erro de
          // sessão), os ramos acima corrigem sozinhos assim que resolverem.
          <div className="flex flex-col items-center justify-center gap-1.5 py-2 text-center text-muted-foreground">
            <Building2 className="h-5 w-5" />
            <div className="text-xs">Este cliente ainda não foi sincronizado com o financeiro (Asaas).</div>
            <button
              type="button"
              onClick={() => onOpenTab("cliente")}
              disabled={!canEditUsers}
              title={!canEditUsers ? "Sem permissão" : undefined}
              className="text-xs font-semibold text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
            >
              Configurar na aba Cliente/Financeiro
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Card 2 — Gestão */}
        <div className="rounded-xl border border-border bg-background p-4">
          <div className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Gestão
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StatBlock
              icon={<IdCard className="h-4 w-4" />}
              label="Contratos"
              value={contract === undefined ? undefined : contract ? 1 : 0}
              onClick={onOpenContract}
              disabled={!canEditUsers}
              title={!canEditUsers ? "Sem permissão" : undefined}
            />
            <StatBlock
              icon={<FileText className="h-4 w-4" />}
              label="Faturas"
              value={invoicesPage?.totalCount}
              onClick={() => onOpenTab("faturamento")}
              disabled={!canEditUsers}
              title={!canEditUsers ? "Sem permissão" : undefined}
            />
            <StatBlock
              icon={<FolderOpen className="h-4 w-4" />}
              label="Documentos"
              value={documents?.length}
              onClick={() => onOpenTab("documentos")}
              disabled={!canEditUsers}
              title={!canEditUsers ? "Sem permissão" : undefined}
            />
          </div>
        </div>

        {/* Card 3 — Controle de Usuário */}
        <div className="rounded-xl border border-border bg-background p-4">
          <div className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Controle de Usuário
          </div>
          <div className="grid grid-cols-5 gap-2">
            <ControlButton
              icon={<LogIn className="h-5 w-5" />}
              label="Logar como"
              tone="neutral"
              disabled={!canImpersonate || !identity}
              title={!canImpersonate ? "Sem permissão" : !identity ? "Carregando…" : undefined}
              onClick={() => setConfirmLoginAsOpen(true)}
            />
            <ControlButton
              icon={<MessageCircle className="h-5 w-5" />}
              label="Login"
              tone="neutral"
              disabled={!canEditUsers || !identity}
              title={!canEditUsers ? "Sem permissão" : !identity ? "Carregando…" : "Enviar login pelo WhatsApp da empresa"}
              onClick={() => setSendLoginOpen(true)}
            />
            <ControlButton
              icon={<Lock className="h-5 w-5" />}
              label="Bloquear"
              tone="warning"
              disabled={!canEditUsers || client.user.disabled}
              title={!canEditUsers ? "Sem permissão" : client.user.disabled ? "Já está bloqueado" : undefined}
              onClick={() => onToggleDisabled(true)}
            />
            <ControlButton
              icon={<Unlock className="h-5 w-5" />}
              label="Desbloquear"
              tone="success"
              disabled={!canEditUsers || !client.user.disabled}
              title={!canEditUsers ? "Sem permissão" : !client.user.disabled ? "Já está ativo" : undefined}
              onClick={() => onToggleDisabled(false)}
            />
            <ControlButton
              icon={<Trash2 className="h-5 w-5" />}
              label="Excluir"
              tone="danger"
              disabled={!canDeleteUsers}
              title={!canDeleteUsers ? "Sem permissão" : undefined}
              onClick={() => {
                setDeleteReason("");
                setDeleteError(null);
                setConfirmDeleteOpen(true);
              }}
            />
          </div>
        </div>
      </div>

      <AlertDialog open={confirmLoginAsOpen} onOpenChange={setConfirmLoginAsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fazer login como este usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que quer fazer login como <strong className="text-foreground">{client.name}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não</AlertDialogCancel>
            <AlertDialogAction disabled={impersonating} onClick={handleLoginAsConfirm}>
              {impersonating ? "Entrando…" : "Sim"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {identity && (
        <SendLoginDialog
          open={sendLoginOpen}
          onOpenChange={setSendLoginOpen}
          userId={identity.id}
          clientName={client.name}
        />
      )}
      <Dialog open={confirmDeleteOpen} onOpenChange={(open) => !deleting && setConfirmDeleteOpen(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir usuário</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o usuário <strong className="text-foreground">{client.name}</strong>?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={`delete-reason-${client.id}`}>Motivo da exclusão</Label>
            <textarea
              id={`delete-reason-${client.id}`}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              maxLength={500}
              rows={3}
              autoFocus
              disabled={deleting}
              placeholder="Ex.: cancelou o contrato, cadastro duplicado, inadimplência…"
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            />
            <div className="text-xs text-muted-foreground">
              Obrigatório. Fica registrado em “Excluídos”, no topo da tela.
            </div>
            {deleteError && <div className="text-xs font-medium text-red-600">{deleteError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={deleting} onClick={() => setConfirmDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={!deleteReasonValid || deleting}
              onClick={handleDeleteConfirm}
            >
              {deleting ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FinanceStat({
  icon,
  label,
  value,
  detail,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-muted/40 p-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="truncate text-sm font-semibold text-foreground">{value}</div>
        <div className="truncate text-[11px] text-muted-foreground">{detail}</div>
      </div>
    </div>
  );
}

function StatBlock({
  icon,
  label,
  value,
  onClick,
  disabled,
  title,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex flex-col items-center justify-center gap-1 rounded-lg bg-muted/40 p-2.5 text-center transition hover:bg-muted/70 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-muted/40"
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-lg font-bold tabular-nums text-foreground">{value ?? "—"}</span>
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
    </button>
  );
}

const CONTROL_BUTTON_TONE = {
  neutral: "bg-muted text-muted-foreground",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  danger: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
} as const;

function ControlButton({
  icon,
  label,
  onClick,
  disabled,
  title,
  tone = "neutral",
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  tone?: keyof typeof CONTROL_BUTTON_TONE;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex flex-col items-center gap-1.5 rounded-lg border border-border bg-background px-2 py-3 text-[11px] font-medium text-foreground transition hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:bg-background"
    >
      <span className={cn("flex h-9 w-9 items-center justify-center rounded-full", CONTROL_BUTTON_TONE[tone])}>
        {icon}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

// Pré-preenchimento ao criar (menu Vendas → Pré-contratos, 2026-10-05):
// o vendedor cria o cliente a partir do cadastro do formulário.
export interface CreateUserPrefill {
  nome: string;
  loginEmail: string;
  telefone: string;
  cli: Partial<CliState>;
}

export function CreateUserDialog({
  open,
  onOpenChange,
  mode = "create",
  initial = null,
  initialTab = "principal",
  prefill = null,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  mode?: "create" | "edit";
  initial?: ClientRow | null;
  initialTab?: string;
  prefill?: CreateUserPrefill | null;
  // Chamado uma vez quando o usuário novo já existe (mesmo que alguma
  // etapa extra — veículos/permissões/cliente — tenha falhado depois).
  onCreated?: (user: User) => void;
}) {
  const isEdit = mode === "edit";
  const [tab, setTab] = useState("principal");
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  // Usuário recém-criado NESTA sessão do dialog (modo "create" original) —
  // sem isso, o operador tinha que salvar, fechar o dialog e reabrir em modo
  // Editar só pra poder configurar Permissões/Cliente/Faturamento (bug
  // relatado 2026-08-31: "quando eu crio um cliente ele não deixa eu colocar
  // permissão", "quando eu tento criar um faturamento ele não deixa
  // também"). `persisted` reflete "este usuário já existe no servidor
  // agora", seja porque o dialog abriu em modo Editar OU porque acabamos de
  // criá-lo — a partir daí ele se comporta como edição pro resto da sessão
  // do dialog, sem precisar fechar/reabrir.
  const [createdUser, setCreatedUser] = useState<User | null>(null);
  const persisted = isEdit || createdUser != null;
  const editId = isEdit ? initial?.user.id : createdUser?.id;
  // Ao editar, busca o usuário fresco no Traccar (GET /api/users/{id}) em vez
  // de reaproveitar só o dado já em cache da listagem.
  const { data: freshUser, isLoading: loadingUser } = useUser(open ? editId : undefined);

  // Avatar do usuário (GPS Drive Backend) — sem relação com empresa, ver
  // gpsdrive-users.service.ts. Mesmo padrão de upload da foto do veículo
  // em routes/index.tsx (fileInputRef + estado de upload + fallback pro
  // ícone genérico se a imagem falhar/ainda não existir).
  const queryClient = useQueryClient();
  const { gpsDriveUser, gpsDriveStatus, user: traccarSessionUser } = useAuth();
  // Empresa criada no Super Master (2026-10-16): o admin dela é GERENTE no
  // Traccar, não administrator — e o Traccar recusa gerente criando
  // administrator. Aí funcionário/admin nascem gerentes também (userLimit
  // -1): enxergam e cadastram dentro da própria empresa, nunca o Traccar todo.
  const canGrantTraccarAdmin = traccarSessionUser?.administrator === true;
  // gpsDriveReady: mesmo critério de UserRow/ClientDetails acima — evita
  // avatar/vínculo/identity disparando com token do GPS Drive Backend ainda
  // vazio logo após F5 (o dialog pode abrir bem cedo no boot da página).
  const gpsDriveReady = gpsDriveStatus !== "loading";
  const { data: avatarData, isFetched: avatarFetched } = useUserAvatar(
    open ? editId : undefined,
    gpsDriveReady,
  );
  const avatarUrl = avatarData?.imageUrls
    ? resolveGpsDriveUrl(avatarData.imageUrls["256"] ?? avatarData.imageUrls.original)
    : undefined;
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => setAvatarFailed(false), [avatarUrl]);
  // Pré-carrega a imagem nova antes de trocar o que é exibido — sem isso,
  // trocar o avatar mostrava um "pisca" (upload → invalida a query → nova
  // URL entra no <img src> antes do navegador ter os bytes prontos)
  // relatado 2026-08-28. displayedAvatarUrl só avança para a URL nova
  // depois que ela terminou de carregar de verdade; até lá continua
  // mostrando a anterior, nunca um estado quebrado/em branco no meio.
  //
  // Além disso (bug relatado 2026-08-28): TODA vez que o dialog Editar
  // Usuário abre, ficava um ícone genérico piscando por alguns segundos
  // até a resposta de /avatar chegar e a imagem nova terminar de baixar —
  // mesmo pra um usuário já visto antes na mesma sessão. localStorage
  // guarda a última URL de avatar confirmada por usuário (chave por
  // editId) e ela é usada pra já abrir com a foto certa na tela, sem
  // esperar a rede; a busca real só confirma/atualiza esse cache embaixo,
  // silenciosamente.
  const avatarCacheKey = editId ? `gpsdrive:avatar-url:${editId}` : undefined;
  const [displayedAvatarUrl, setDisplayedAvatarUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!open || !avatarCacheKey) {
      setDisplayedAvatarUrl(undefined);
      return;
    }
    try {
      setDisplayedAvatarUrl(localStorage.getItem(avatarCacheKey) ?? undefined);
    } catch {
      setDisplayedAvatarUrl(undefined);
    }
  }, [open, avatarCacheKey]);
  useEffect(() => {
    if (!avatarFetched) return; // ainda carregando — mantém o que já está na tela (cache)
    if (!avatarUrl) {
      // resposta confirmada: este usuário não tem foto — não deixa uma
      // foto antiga em cache aparecendo pra ele da próxima vez.
      setDisplayedAvatarUrl(undefined);
      if (avatarCacheKey) {
        try {
          localStorage.removeItem(avatarCacheKey);
        } catch {
          // ignora — pior caso é um flash na próxima abertura
        }
      }
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setDisplayedAvatarUrl(avatarUrl);
      if (avatarCacheKey) {
        try {
          localStorage.setItem(avatarCacheKey, avatarUrl);
        } catch {
          // localStorage indisponível/cheio — sem cache, mas sem quebrar o fluxo
        }
      }
    };
    img.onerror = () => {
      if (!cancelled) setAvatarFailed(true);
    };
    img.src = avatarUrl;
    return () => {
      cancelled = true;
    };
  }, [avatarUrl, avatarFetched, avatarCacheKey]);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const handlePickAvatar = async (file?: File) => {
    if (!file || !editId) return;
    setAvatarUploading(true);
    try {
      await gpsDriveUsersService.uploadAvatar(editId, file);
      await queryClient.invalidateQueries({ queryKey: queryKeys.userAvatar(editId) });
    } catch (err) {
      toast.error("Não foi possível trocar a imagem do usuário", {
        description:
          err instanceof GpsDriveApiError && err.status === 413
            ? "A imagem é muito grande. O tamanho máximo permitido é 5 MB."
            : undefined,
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // PRINCIPAL — mapeia somente campos que existem de verdade no Traccar
  // (GET/POST/PUT /api/users). Sem "role"/"grupo" inventado.
  const [nome, setNome] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [administrador, setAdministrador] = useState(false);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [, setSomenteLeituraDispositivos] = useState(false);
  const [desabilitado, setDesabilitado] = useState(false);
  const [limiteDispEnabled, setLimiteDispEnabled] = useState(false);
  const [limiteDisp, setLimiteDisp] = useState("");
  const [limiteUsuariosEnabled, setLimiteUsuariosEnabled] = useState(false);
  const [limiteUsuarios, setLimiteUsuarios] = useState("");
  const [validadeEnabled, setValidadeEnabled] = useState(false);
  const [validade, setValidade] = useState("");
  const [senha, setSenha] = useState("");
  const [confSenha, setConfSenha] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [showConfSenha, setShowConfSenha] = useState(false);

  // VEÍCULOS — vínculo real via POST/DELETE /api/permissions. Fonte da
  // LISTA (checkboxes): GET /api/v1/vehicles (frota "reivindicada" da
  // empresa, via vehicle_metadata.company_id) UNIDA aos devices já
  // vinculados ao usuário (GET /api/v1/permissions/users/:id/vehicles) —
  // union necessária porque um veículo pode estar vinculado (tc_user_device)
  // sem nunca ter passado por Cadastrar/Editar veículo (sem vehicle_metadata
  // ainda), e nesse caso não aparece na frota da empresa; sem essa união
  // ele contava como selecionado mas sumia da lista visível (ver diagnóstico
  // 2026-08-26). Nunca useVehicles()/GET /api/devices — reflete a sessão de
  // quem está logado, não a empresa do usuário sendo editado. Fonte do
  // ESTADO dos checkboxes é sempre o vínculo real (linkedDeviceIds);
  // "vincSelected" é o que o operador está escolhendo na tela, seedado a
  // partir dele — no Salvar, só a diferença entre os dois é enviada (ver
  // handleSave).
  const syncDevices = useSyncUserDevicePermissions();
  // Frota carrega mesmo sem `persisted` — precisa aparecer na aba VEÍCULOS
  // já no cadastro de um usuário novo, pra dar pra escolher o vínculo ANTES
  // de existir um userId de verdade (o vínculo em si só é enviado no
  // handleSave, usando o id do usuário recém-criado — ver `selected`
  // abaixo). `linkedDevices` abaixo continua atrás de `persisted`: usuário
  // novo não tem vínculo nenhum ainda, não faz sentido buscar.
  const { data: companyFleet = [] } = useCompanyFleet(open);
  const { data: linkedDevices, isLoading: loadingDeviceLinks } = useDevicePermissions(
    open && persisted ? editId : undefined,
    gpsDriveReady,
  );
  const linkedDeviceIds = useMemo(() => (linkedDevices ?? []).map((d) => String(d.id)), [linkedDevices]);
  const fleetRoster = useMemo(() => {
    const byId = new Map(companyFleet.map((v) => [v.id, v]));
    for (const d of linkedDevices ?? []) {
      if (!byId.has(d.id)) byId.set(d.id, d);
    }
    return [...byId.values()];
  }, [companyFleet, linkedDevices]);
  const [vincSelected, setVincSelected] = useState<Record<string, boolean>>({});
  const [vincQuery, setVincQuery] = useState("");
  const toggleVinc = (id: string) => setVincSelected((p) => ({ ...p, [id]: !p[id] }));

  // Âncora (pedido 2026-09-04, "no perfil do usuário tem que ter o menu de
  // configurar âncora") — aba VEÍCULOS só tem TraccarDeviceRef (sem
  // lat/lng, ver comentário em useCompanyFleet acima), então a posição real
  // só é buscada sob demanda quando o operador clica no ícone de uma linha
  // (nunca para a lista inteira de uma vez).
  const canAnchor = usePermission("devices", "anchor");
  const [anchorTargetDevice, setAnchorTargetDevice] = useState<TraccarDeviceRef | null>(null);
  const { data: anchorPosition, isFetching: anchorPositionLoading } = useVehicleCompanyPosition(
    anchorTargetDevice?.id,
    anchorTargetDevice !== null,
  );
  const filteredDevices = fleetRoster.filter((v) => {
    const q = vincQuery.trim().toLowerCase();
    if (!q) return true;
    const plate = typeof v.attributes?.plate === "string" ? v.attributes.plate : "";
    const driver = typeof v.attributes?.driver === "string" ? v.attributes.driver : "";
    return (
      v.name.toLowerCase().includes(q) ||
      plate.toLowerCase().includes(q) ||
      driver.toLowerCase().includes(q) ||
      (v.uniqueId ?? "").toLowerCase().includes(q)
    );
  });
  const vincCount = Object.values(vincSelected).filter(Boolean).length;

  // CLIENTE — Cliente/Financeiro (CPF/CNPJ, contrato, cobrança) — real
  // desde 2026-08-27, GET/PUT /users/:userId/permissions (GPS Drive
  // Backend próprio, ver gpsdrive-client-profile.service.ts). `cli` é a
  // cópia local editável (mesmo padrão de editablePermissions): sincroniza
  // com o servidor via useEffect abaixo (ver useClientProfile) e só grava
  // de verdade ao clicar "Salvar dados do cliente" — nunca a cada toggle
  // de campo. reset() (adiante) zera pro modo "criar usuário".
  const [cli, setCli] = useState({
    cpfCnpj: "",
    razaoSocial: "",
    nomeContrato: "",
    rg: "",
    dataNascimento: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    celular: "",
    valorMensalidade: "",
    foneFixo: "",
    diaVencimento: "",
    emailCobranca: "",
    rendaFaturamento: "",
    numeroSuporte: "",
    notas: "",
  });
  const setCliField = (k: keyof typeof cli, v: string) =>
    setCli((prev) => ({ ...prev, [k]: v }));
  // Pessoas autorizadas (pedido 2026-10-08) — fora do `cli` porque é uma
  // lista, não texto; salva junto com a aba Cliente.
  const [authContacts, setAuthContacts] = useState<AuthorizedContact[]>([]);

  // PERMISSÕES — reais por USUÁRIO (GPS Drive Backend), não mais por papel
  // (decisão 2026-08-26, revertida no mesmo dia após feedback: a matriz
  // por papel confundia "Grupo" com "capacidades do usuário" — ver
  // auditoria da etapa). "Grupo" (gpsDriveRoles) continua existindo só
  // pro select do topo; identity resolve o traccarUserId sendo editado
  // pro userId interno (UUID) que o backend exige em
  // GET/PUT /users/:userId/permissions — provisiona na hora se for a
  // primeira vez (ver backend). editablePermissions é uma cópia local
  // editável (só troca de verdade no servidor ao clicar "Salvar") —
  // nunca salva a cada toggle isolado.
  const { data: gpsDriveRoles = [] } = useGpsDriveRoles(open);
  const {
    data: identity,
    isError: identityFailed,
    refetch: refetchIdentity,
  } = useResolveUserByTraccarId(persisted ? editId : undefined, open && persisted && gpsDriveReady);
  // Pedido 2026-09-08: "quando eu entro em permissões dentro de um usuário
  // ele aparece sem nenhuma permissão habilitada, passa uns segundos ai
  // aparece as permissões" — isLoading aqui cobre a SEGUNDA consulta
  // sequencial (identity resolve primeiro, só depois esta dispara), que o
  // guard de baixo (persisted && !identity) não cobria: o accordion
  // renderizava com editablePermissions ainda no blankPermissions() default
  // durante essa janela.
  const { data: userPermissions, isLoading: userPermissionsLoading } = useUserPermissions(identity?.id);
  const updateUserPermissions = useUpdateUserPermissions();
  // CLIENTE — mesmo padrão de identity/userPermissions acima: identity.id
  // (userId interno) é o mesmo usado por GET/PUT /users/:userId/permissions,
  // reaproveitado aqui pra client-profile (nunca o traccarUserId).
  const { data: clientProfile } = useClientProfile(identity?.id);
  const updateClientProfile = useUpdateClientProfile();
  useEffect(() => {
    if (clientProfile !== undefined) {
      setCli(clientProfileToCliState(clientProfile));
      setAuthContacts(clientProfile?.authorizedContacts ?? []);
    }
  }, [clientProfile]);
  // CONTRATO — modelo de contrato + signatário selecionados pra este
  // cliente (2026-08-28, primeiro backend real desta aba; antes disso o
  // dropdown de contrato tinha uma única opção fixa "c1" e nada era
  // salvo). Modelos/signatários vêm dos mesmos cadastros de Administração
  // > Conta & Integrações > Contrato (ver admin.contract.tsx).
  const { data: contractTemplates } = useContractTemplates();
  const { data: contractSignatories } = useContractSignatories();
  const { data: clientContract } = useClientContract(identity?.id);
  const updateClientContract = useUpdateClientContract();
  // Editar/excluir só enquanto NÃO assinado (pedido 2026-10-16). Assinado
  // fica travado — pra mudar, "Novo contrato" em Vendas.
  const contractSigned = !!clientContract?.signedAt;
  const deleteClientContract = useDeleteClientContract();
  // Aba Contrato em lista (pedido 2026-10-23: "tá confuso... ter um botão de
  // criar contrato, ver os contratos gerados, assinados, pendentes, e só o
  // admin pode excluir"). Formulário abre em popup; "current" = contrato
  // atual, senão o id de um contrato anterior.
  const { data: contractVersions = [] } = useClientContractVersions(identity?.id);
  const startNewContractVersion = useStartNewClientContractVersion();
  const deleteContractVersion = useDeleteClientContractVersion();
  const [contractFormMode, setContractFormMode] = useState<"new" | "edit" | null>(null);
  const [signPanelOpen, setSignPanelOpen] = useState(false);
  const [confirmDeleteContract, setConfirmDeleteContract] = useState<string | null>(null);
  const [viewingContract, setViewingContract] = useState<{ versionId: string | null } | null>(null);
  const handleDeleteClientContract = async () => {
    if (!identity || !confirmDeleteContract) return;
    try {
      if (confirmDeleteContract === "current") await deleteClientContract.mutateAsync(identity.id);
      else await deleteContractVersion.mutateAsync({ userId: identity.id, versionId: confirmDeleteContract });
      setConfirmDeleteContract(null);
    } catch (err) {
      toast.error("Não foi possível excluir o contrato", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  const [contractForm, setContractForm] = useState({
    templateId: "",
    signatoryIds: [] as string[],
    contractValue: "",
    recurrenceValue: "",
    // Veículos gravados no contrato (2026-10-14) — digitados ou escolhidos
    // dos cadastrados; vazio = usa os vinculados ao cliente.
    vehicles: [] as ContractVehicle[],
  });
  useEffect(() => {
    if (clientContract === undefined) return;
    const savedSignatories = contractSignatoryIds(clientContract);
    setContractForm({
      templateId: clientContract?.templateId ?? "",
      signatoryIds:
        savedSignatories.length || clientContract?.signedAt ? savedSignatories : defaultSignatoryIds(contractSignatories),
      vehicles: clientContract?.vehicles ?? [],
      contractValue: clientContract?.contractValue != null ? String(clientContract.contractValue).replace(".", ",") : "",
      recurrenceValue:
        clientContract?.recurrenceValue != null ? String(clientContract.recurrenceValue).replace(".", ",") : "",
    });
  }, [clientContract]); // eslint-disable-line react-hooks/exhaustive-deps
  // Signatários podem chegar depois do contrato — completa só se ainda vazio.
  useEffect(() => {
    if (!contractSignatories?.length || clientContract === undefined || clientContract?.signedAt) return;
    if (contractSignatoryIds(clientContract).length) return;
    setContractForm((s) => (s.signatoryIds.length ? s : { ...s, signatoryIds: defaultSignatoryIds(contractSignatories) }));
  }, [contractSignatories, clientContract]);
  const handleSaveClientContract = async () => {
    if (!identity) return;
    if (!contractForm.templateId) {
      toast.error("Selecione um modelo de contrato");
      return;
    }
    try {
      // Criar com o atual já assinado: o assinado vira "anterior" e este
      // nasce como versão nova.
      if (contractFormMode === "new" && contractSigned) await startNewContractVersion.mutateAsync(identity.id);
      await updateClientContract.mutateAsync({
        userId: identity.id,
        data: {
          templateId: contractForm.templateId || null,
          signatoryId: contractForm.signatoryIds[0] ?? null,
          signatoryIds: contractForm.signatoryIds,
          contractValue: contractForm.contractValue ? Number(contractForm.contractValue.replace(",", ".")) : null,
          recurrenceValue: contractForm.recurrenceValue ? Number(contractForm.recurrenceValue.replace(",", ".")) : null,
          vehicles: contractForm.vehicles.filter((v) => v.plate.trim() || v.model.trim()),
        },
      });
      setContractFormMode(null);
      setSignPanelOpen(true);
    } catch (err) {
      toast.error("Não foi possível salvar o contrato", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  // "Gerar link de assinatura" — única forma de assinatura desde 2026-09-11
  // ("Marcar como assinado"/confirmação manual foi removido): o próprio
  // cliente abre /contrato/assinar/:token sem login e confirma
  // dados/veículos/localização/foto/assinatura sozinho. A URL é derivada
  // direto de clientContract.signingToken (não guardada em state local) —
  // sobrevive a um F5 e atualiza sozinha quando o token muda.
  const generateSigningLink = useGenerateContractSigningLink();
  const sendSigningLinkByWhatsapp = useSendSigningLinkByWhatsapp();
  const { data: whatsappIntegration } = useWhatsappIntegration();
  const signingLinkUrl = clientContract?.signingToken
    ? `${window.location.origin}/contrato/assinar/${clientContract.signingToken}`
    : null;
  const handleGenerateSigningLink = async () => {
    if (!identity) return;
    try {
      await generateSigningLink.mutateAsync(identity.id);
      toast.success("Link de assinatura gerado");
    } catch (err) {
      toast.error("Não foi possível gerar o link de assinatura", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  const handleCopySigningLink = async () => {
    if (!signingLinkUrl) return;
    try {
      await navigator.clipboard.writeText(signingLinkUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };
  const handleSendSigningLinkWhatsapp = async () => {
    if (!identity || !signingLinkUrl) return;
    try {
      await sendSigningLinkByWhatsapp.mutateAsync({ userId: identity.id, url: signingLinkUrl });
      toast.success("Link enviado por WhatsApp");
    } catch (err) {
      toast.error("Não foi possível enviar o link por WhatsApp", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  // DOCUMENTOS — arquivos reais do cliente (contrato, RG/CNH, comprovante
  // de residência/renda etc), ver gpsdrive-client-documents.service.ts.
  // Antes desta etapa (2026-08-27) a aba era 100% de mentira: botão
  // "Enviar documentos" não fazia nada, lista sempre "Sem dados.".
  const { data: clientDocuments, isLoading: loadingDocuments } = useClientDocuments(identity?.id);
  const uploadDocument = useUploadClientDocument();
  const deleteDocument = useDeleteClientDocument();
  const documentFileInputRef = useRef<HTMLInputElement>(null);
  const [newDocumentCategory, setNewDocumentCategory] = useState<ClientDocumentCategory>("outro");
  const handleDocumentFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !identity) return;
    try {
      await uploadDocument.mutateAsync({ userId: identity.id, file, category: newDocumentCategory });
      toast.success("Documento enviado");
    } catch (err) {
      toast.error("Não foi possível enviar o documento", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  const handleDeleteDocument = (doc: ClientDocument) => {
    if (!identity) return;
    if (!confirm(`Excluir o documento "${doc.fileName}"? Esta ação não pode ser desfeita.`)) return;
    deleteDocument.mutate(
      { userId: identity.id, documentId: doc.id },
      {
        onSuccess: () => toast.success("Documento excluído"),
        onError: (err) =>
          toast.error("Não foi possível excluir o documento", {
            description: err instanceof GpsDriveApiError ? err.message : undefined,
          }),
      },
    );
  };
  // FATURAMENTO — cobranças reais do cliente no Asaas (ver
  // gpsdrive-billing.service.ts). invoicesOffset zera sempre que o dialog
  // reabre pra outro usuário (mesmo padrão de reset() abaixo).
  const [invoicesOffset, setInvoicesOffset] = useState(0);
  const { data: invoicesPage, isLoading: loadingInvoices } = useInvoices(identity?.id, invoicesOffset);
  const createInvoice = useCreateInvoice();
  const [showNewInvoiceForm, setShowNewInvoiceForm] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    value: "",
    dueDate: "",
    billingType: "PIX" as InvoiceBillingType,
    description: "",
  });
  const handleCreateInvoice = async () => {
    if (!identity) return;
    const value = Number(newInvoice.value.replace(",", "."));
    if (!value || value <= 0 || !newInvoice.dueDate) {
      toast.error("Preencha valor e vencimento");
      return;
    }
    try {
      await createInvoice.mutateAsync({
        userId: identity.id,
        data: {
          value,
          dueDate: newInvoice.dueDate,
          billingType: newInvoice.billingType,
          description: newInvoice.description.trim() || undefined,
        },
      });
      toast.success("Cobrança criada no Asaas");
      setNewInvoice({ value: "", dueDate: "", billingType: "PIX", description: "" });
      setShowNewInvoiceForm(false);
      setInvoicesOffset(0);
    } catch (err) {
      toast.error("Não foi possível criar a cobrança", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };

  // "Editar"/"Excluir" (aba FATURAMENTO) — o Asaas só aceita as duas
  // operações em cobranças ainda não pagas (ver billing.service.ts no
  // backend); mesmo padrão de estado/handler de handleCreateInvoice acima.
  const updateInvoice = useUpdateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({
    value: "",
    dueDate: "",
    billingType: "PIX" as InvoiceBillingType,
    description: "",
  });
  const openEditInvoice = (inv: Invoice) => {
    setEditingInvoice(inv);
    setEditInvoiceForm({
      value: inv.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 }),
      dueDate: inv.dueDate,
      billingType: (inv.billingType as InvoiceBillingType) || "PIX",
      description: inv.description ?? "",
    });
  };
  const handleUpdateInvoice = async () => {
    if (!identity || !editingInvoice) return;
    const value = Number(editInvoiceForm.value.replace(/\./g, "").replace(",", "."));
    if (!value || value <= 0 || !editInvoiceForm.dueDate) {
      toast.error("Preencha valor e vencimento");
      return;
    }
    try {
      await updateInvoice.mutateAsync({
        userId: identity.id,
        invoiceId: editingInvoice.id,
        data: {
          value,
          dueDate: editInvoiceForm.dueDate,
          billingType: editInvoiceForm.billingType,
          description: editInvoiceForm.description.trim() || undefined,
        },
      });
      toast.success("Cobrança atualizada");
      setEditingInvoice(null);
    } catch (err) {
      toast.error("Não foi possível editar a cobrança", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  const handleDeleteInvoice = (inv: Invoice) => {
    if (!identity) return;
    if (
      !confirm(
        `Excluir esta cobrança de R$ ${inv.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}? Esta ação não pode ser desfeita.`,
      )
    ) {
      return;
    }
    deleteInvoice.mutate(
      { userId: identity.id, invoiceId: inv.id },
      {
        onSuccess: () => toast.success("Cobrança excluída"),
        onError: (err) =>
          toast.error("Não foi possível excluir a cobrança", {
            description: err instanceof GpsDriveApiError ? err.message : undefined,
          }),
      },
    );
  };
  // "Enviar para WhatsApp" — ainda sem integração própria (ver pedido do
  // produto); abre o WhatsApp Web/app já com o número cadastrado do
  // cliente (cli.celular, aba CLIENTE) e uma mensagem pronta com o link
  // da fatura, pro operador só confirmar o envio. Sem número cadastrado,
  // avisa em vez de abrir o WhatsApp sem destinatário.
  const handleSendInvoiceWhatsApp = (inv: Invoice) => {
    const digits = cli.celular.replace(/\D/g, "");
    if (!digits) {
      toast.error("Este cliente não tem celular cadastrado", {
        description: "Preencha o campo Celular na aba CLIENTE para poder enviar pelo WhatsApp.",
      });
      return;
    }
    const phone = digits.length <= 11 ? `55${digits}` : digits;
    const valueLabel = inv.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
    const dueLabel = new Date(`${inv.dueDate}T00:00:00`).toLocaleDateString("pt-BR");
    const lines = [
      `Olá! Segue sua fatura${inv.description ? ` (${inv.description})` : ""}.`,
      `Valor: R$ ${valueLabel}`,
      `Vencimento: ${dueLabel}`,
    ];
    if (inv.invoiceUrl) lines.push(inv.invoiceUrl);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  // "Recorrência" — uma única assinatura no Asaas cobrindo os veículos
  // marcados (valor somado, ver handleCreateSubscription), já que cada
  // veículo deste cliente pode estar num plano diferente. Lista de
  // veículos vem do vínculo real (linkedDevices), mesma fonte da aba
  // VEÍCULOS — nunca a frota inteira da empresa.
  const [showRecurrenceDialog, setShowRecurrenceDialog] = useState(false);
  const [recurrence, setRecurrence] = useState({
    cycle: "MONTHLY" as SubscriptionCycle,
    billingType: "PIX" as InvoiceBillingType,
    nextDueDate: "",
    interestValue: "",
    fineType: "PERCENTAGE" as "FIXED" | "PERCENTAGE",
    fineValue: "",
    discountDays: "",
    discountType: "PERCENTAGE" as "FIXED" | "PERCENTAGE",
    discountValue: "",
  });
  // Pedido 2026-09-01: "se eu colocar dia 20 [em Dia de vencimento (Fatura),
  // aba Cliente] quando for criar uma recorrência já tem que ficar com o
  // dia 20, aí eu só escolho o mês e ano" + "caso o cliente queira alterar
  // a data de vencimento é só editar o dia do vencimento e ir em
  // recorrência aí já deve aparecer disponível ali" — então isto não só
  // preenche o dia na primeira abertura como também RESSINCRONIZA o dia
  // (preservando mês/ano já escolhidos) toda vez que "Dia de vencimento"
  // mudar enquanto o formulário de recorrência está aberto. Só reage a
  // mudanças em diaVencimento — se o admin editar a data manualmente sem
  // mexer em diaVencimento, a escolha dele não é sobrescrita.
  useEffect(() => {
    if (!showRecurrenceDialog) return;
    const day = Number(cli.diaVencimento);
    if (!day) return;
    setRecurrence((s) => {
      const [yStr, mStr] = s.nextDueDate ? s.nextDueDate.split("-") : [];
      let year = yStr ? Number(yStr) : NaN;
      let month = mStr ? Number(mStr) - 1 : NaN;
      if (Number.isNaN(year) || Number.isNaN(month)) {
        const now = new Date();
        year = now.getFullYear();
        month = now.getMonth();
        if (now.getDate() > day) {
          month += 1;
          if (month > 11) {
            month = 0;
            year += 1;
          }
        }
      }
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const clampedDay = Math.min(day, daysInMonth);
      const nextDueDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`;
      return nextDueDate === s.nextDueDate ? s : { ...s, nextDueDate };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecurrenceDialog, cli.diaVencimento]);
  const [vehiclePlans, setVehiclePlans] = useState<
    Record<string, { selected: boolean; planId: string; valor: string }>
  >({});
  // Planos reais cadastrados em Administração > Conta & Integrações >
  // Planos (ver admin.plans.tsx) — substitui os 4 tiers fixos sem preço
  // que existiam antes (decisão de produto 2026-08-27 revertida
  // 2026-08-28: agora o valor vem do plano escolhido, não é mais digitado
  // do zero pra cada veículo).
  const { data: plans } = usePlans();
  // Etiqueta "tem assistência" por veículo (aba VEÍCULOS) — só equipe.
  const { data: assistanceByDevice } = useAssistanceVehiclePlans(!isRestrictedAccountType(gpsDriveUser?.accountType));
  useEffect(() => {
    if (!showRecurrenceDialog) return;
    setVehiclePlans((prev) => {
      const next = { ...prev };
      for (const d of linkedDevices ?? []) {
        const id = String(d.id);
        if (!next[id]) next[id] = { selected: false, planId: "", valor: "" };
      }
      return next;
    });
  }, [showRecurrenceDialog, linkedDevices]);
  const selectVehiclePlan = (deviceId: string, planId: string) => {
    const selectedPlan = plans?.find((p) => p.id === planId);
    setVehiclePlans((s) => ({
      ...s,
      [deviceId]: {
        ...(s[deviceId] ?? { selected: false, planId: "", valor: "" }),
        planId,
        valor: selectedPlan ? String(selectedPlan.value).replace(".", ",") : (s[deviceId]?.valor ?? ""),
      },
    }));
  };
  const createSubscription = useCreateSubscription();
  const selectedVehiclePlans = (linkedDevices ?? [])
    .map((d) => ({ device: d, plan: vehiclePlans[String(d.id)] }))
    .filter((v) => v.plan?.selected);
  const recurrenceTotalValue = selectedVehiclePlans.reduce(
    (sum, v) => sum + (Number(v.plan.valor.replace(",", ".")) || 0),
    0,
  );
  // Plano escolhido aqui vira o plano do veículo (pedido 2026-10-14: "escolho
  // o plano na Nova recorrência e no contrato fica sem plano") — antes só
  // servia pro valor da cobrança. Grava vehicle_metadata.planId (contrato,
  // assinatura e Editar dispositivo leem de lá) e completa o plano dos
  // veículos já salvos no contrato (mesma placa, plano vazio).
  const savePlansToVehicles = async (
    picked: { device: { id: number; attributes?: Record<string, unknown> }; plan: { planId: string } }[],
  ) => {
    const withPlan = picked.filter((v) => v.plan.planId);
    if (!withPlan.length) return;
    const results = await Promise.allSettled(
      withPlan.map((v) => vehicleMetadataService.update(v.device.id, { planId: v.plan.planId })),
    );
    for (const v of withPlan) queryClient.invalidateQueries({ queryKey: queryKeys.vehicleMetadata(v.device.id) });
    queryClient.invalidateQueries({ queryKey: ["contract-print-vehicle-plans"] });
    if (results.some((r) => r.status === "rejected")) {
      toast.error("Recorrência criada, mas não foi possível gravar o plano em algum veículo", {
        description: "Confira em Editar dispositivo → Plano.",
      });
    }
    const saved = clientContract?.vehicles ?? [];
    if (!identity || !clientContract || !saved.length || clientContract.signedAt) return;
    const key = (s: string) => s.replace(/[^a-z0-9]/gi, "").toUpperCase();
    let changed = false;
    const vehicles = saved.map((cv) => {
      if (cv.plan.trim()) return cv;
      const hit = withPlan.find((v) => {
        const plate = typeof v.device.attributes?.plate === "string" ? v.device.attributes.plate : "";
        return !!plate && key(plate) === key(cv.plate);
      });
      const name = hit ? plans?.find((p) => p.id === hit.plan.planId)?.name : undefined;
      if (!name) return cv;
      changed = true;
      return { ...cv, plan: name };
    });
    if (!changed) return;
    await updateClientContract
      .mutateAsync({
        userId: identity.id,
        data: {
          templateId: clientContract.templateId,
          signatoryId: clientContract.signatoryId,
          signatoryIds: contractSignatoryIds(clientContract),
          contractValue: clientContract.contractValue,
          recurrenceValue: clientContract.recurrenceValue,
          vehicles,
        },
      })
      .catch(() => undefined);
  };
  const handleCreateSubscription = async () => {
    if (!identity) return;
    if (!selectedVehiclePlans.length) {
      toast.error("Selecione ao menos um veículo");
      return;
    }
    if (!recurrenceTotalValue || recurrenceTotalValue <= 0) {
      toast.error("Informe o valor do plano de cada veículo selecionado");
      return;
    }
    if (!recurrence.nextDueDate) {
      toast.error("Informe a data de vencimento");
      return;
    }
    const description = selectedVehiclePlans
      .map(({ device, plan }) => {
        // Só "Plano - PLACA" (pedido 2026-10-15): o nome do veículo
        // normalmente já é a placa e saía duplicado.
        const plate = typeof device.attributes?.plate === "string" ? device.attributes.plate.trim() : "";
        const label = plate || device.name;
        const planName = plans?.find((p) => p.id === plan.planId)?.name;
        return planName ? `${planName} - ${label}` : label;
      })
      .join(", ");
    try {
      await createSubscription.mutateAsync({
        userId: identity.id,
        data: {
          cycle: recurrence.cycle,
          billingType: recurrence.billingType,
          value: recurrenceTotalValue,
          nextDueDate: recurrence.nextDueDate,
          description,
          interestValue: recurrence.interestValue ? Number(recurrence.interestValue) : undefined,
          fineType: recurrence.fineValue ? recurrence.fineType : undefined,
          fineValue: recurrence.fineValue ? Number(recurrence.fineValue) : undefined,
          discountDays: recurrence.discountValue ? Number(recurrence.discountDays) || 0 : undefined,
          discountType: recurrence.discountValue ? recurrence.discountType : undefined,
          discountValue: recurrence.discountValue ? Number(recurrence.discountValue) : undefined,
        },
      });
      toast.success("Recorrência criada no Asaas");
      void savePlansToVehicles(selectedVehiclePlans);
      setShowRecurrenceDialog(false);
      setVehiclePlans({});
      setRecurrence({
        cycle: "MONTHLY",
        billingType: "PIX",
        nextDueDate: "",
        interestValue: "",
        fineType: "PERCENTAGE",
        fineValue: "",
        discountDays: "",
        discountType: "PERCENTAGE",
        discountValue: "",
      });
    } catch (err) {
      toast.error("Não foi possível criar a recorrência", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };

  const restoreDefaultPermissions = useRestoreDefaultUserPermissions();
  const changeUserRole = useChangeUserRole();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  // Começa com o catálogo inteiro (tudo desabilitado, mesma baseline que o
  // backend usa no provisionamento — ver traccar-user-provisioning.ts) em
  // vez de `[]`, pra dar pra marcar permissões ANTES do usuário existir de
  // verdade (usuário novo não tem userPermissions do servidor ainda pra
  // popular isto). Vira as permissões reais assim que `userPermissions`
  // carrega (usuário existente/recém-criado).
  const [editablePermissions, setEditablePermissions] = useState<PermissionCategoryState[]>(blankPermissions);
  useEffect(() => {
    if (userPermissions) setEditablePermissions(userPermissions);
  }, [userPermissions]);
  // "Grupo do usuário" (pedido 2026-09-03) — papel fixo, SEPARADO do Grupo
  // de permissão (pendingRoleId abaixo). Só decide qual "gaveta" de Grupos
  // de permissão aparece pro select (ver visibleRoles mais abaixo).
  const [accountType, setAccountType] = useState<AccountType>("usuario");
  useEffect(() => {
    setAccountType(identity?.accountType ?? "usuario");
  }, [identity?.accountType]);
  const changeAccountType = useChangeAccountType();

  // Sub-usuários (pedido 2026-09-10) — só dispara quando a aba USUÁRIOS
  // pode de fato existir (mesmo critério do tab trigger, ver TabsList
  // abaixo), pra não gastar uma chamada à toa em toda abertura de Editar
  // Usuário.
  const { data: subUsers = [], isLoading: loadingSubUsers } = useSubUsersByCreator(
    open && persisted && accountType === "usuario" ? identity?.id : undefined,
  );
  const deleteSubUser = useDeleteSubUser();
  const [editingSubUserId, setEditingSubUserId] = useState<string | null>(null);
  const [deletingSubUserId, setDeletingSubUserId] = useState<string | null>(null);
  // Detalhes de sessão/push (pedido 2026-09-12: cliente reclamou de não
  // receber notificação, e não havia como conferir se aquele sub-usuário
  // específico tinha sequer logado ou gerado token de push sem abrir uma
  // consulta manual no banco). Reaproveita o mesmo UserConnectedDevicesTab
  // da aba DISPOSITIVOS CONECTADOS do usuário principal, num dialog à
  // parte — SubUserFormDialog é só formulário, não tem abas.
  const [subUserDetailsId, setSubUserDetailsId] = useState<string | null>(null);
  const handleDeleteSubUser = async (id: string, name: string) => {
    if (!confirm(`Excluir o usuário "${name}"? Essa pessoa perde o acesso imediatamente.`)) return;
    setDeletingSubUserId(id);
    try {
      await deleteSubUser.mutateAsync(id);
      toast.success(`Usuário "${name}" excluído.`);
    } catch (err) {
      toast.error("Não foi possível excluir agora.", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setDeletingSubUserId(null);
    }
  };

  // Revenda (pedido 2026-09-07) — só existe na CRIAÇÃO de um usuário novo
  // (nunca ao editar um já existente, ver filtro de accountTypeOptions mais
  // abaixo): cria uma empresa filha + este usuário como o Administrator
  // dela. companyHasParent vem da PRÓPRIA empresa do operador logado — se
  // ela já for uma revenda, "Revenda" nem aparece no select (único nível).
  const { data: myCompany } = useMyCompany();
  const companyHasParent = !!myCompany?.parentCompanyId;
  const [resellerCompanyName, setResellerCompanyName] = useState("");
  const createReseller = useCreateReseller();
  // Administrador "de verdade" (papel Administrator ou meta-permissão
  // administrador) vs. quem gerencia usuários só pela permissão granular
  // Usuários→Editar — pedido 2026-09-23: Funcionário (nunca tem o papel
  // Administrator) não conseguia escolher/criar Grupo nem aplicar
  // permissão. O não-administrador nunca vê as opções que concederiam
  // acesso de administrador (Grupo Administrator, Grupo do usuário Admin,
  // categoria administrador) nem mexe em quem já é administrador — mesmas
  // travas do backend (assertCanGrantAdmin).
  const hasAdministradorPermission = usePermission("administrador");
  const isFullAdmin = gpsDriveUser?.role === "Administrator" || hasAdministradorPermission;
  // Excluir contrato: só administrador (backend também trava).
  const canDeleteContract = isFullAdmin || gpsDriveUser?.accountType === "admin";
  const canEditUsersPermission = usePermission("users", "edit");
  const targetIsAdmin =
    persisted &&
    (identity?.accountType === "admin" ||
      gpsDriveRoles.find((r) => r.id === identity?.roleId)?.name === "Administrator" ||
      !!userPermissions?.find((c) => c.category === "administrador")?.enabled);
  const canManagePermissions = isFullAdmin || (canEditUsersPermission && !targetIsAdmin);
  const accountTypeOptions = ACCOUNT_TYPES.filter(
    (t) =>
      (t !== "revenda" || (!persisted && !companyHasParent)) &&
      (isFullAdmin || (t !== "admin" && t !== "revenda")),
  );

  const [pendingRoleId, setPendingRoleId] = useState("");
  useEffect(() => {
    setPendingRoleId(identity?.roleId ?? "");
  }, [identity?.roleId]);
  // "Criar/Editar grupo" — form próprio (nome + accordion de permissões),
  // nunca toca editablePermissions do usuário sendo editado. `editingGroupId`
  // null = criando um Grupo novo; preenchido = editando o Grupo com esse id
  // (pré-carregado via GET /roles/:id, ver openEditGroup — só existe desde
  // 2026-09-03, antes não tinha como ler o conteúdo de um Grupo sem aplicar
  // a um usuário real). `groupFormScope` é a "gaveta" (Role#scope) que o
  // Grupo sendo criado/editado vai levar — pra criar, vem do accountType
  // atual (nunca escolhido à mão); pra editar, vem do próprio Grupo (GET
  // /roles/:id), preservado mesmo que o accountType do usuário ATUAL seja
  // diferente de quem originalmente criou aquele Grupo.
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [groupFormScope, setGroupFormScope] = useState<RoleScope>("geral");
  const [loadingGroupDetail, setLoadingGroupDetail] = useState(false);
  const [deleteGroupConfirmOpen, setDeleteGroupConfirmOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupPermissions, setNewGroupPermissions] = useState<PermissionCategoryState[]>(
    blankPermissions(),
  );
  // Grupos que fazem sentido pro accountType atual — "funcionario" nunca
  // mistura com os Grupos "geral" (Admin/Usuário), pedido explícito
  // 2026-09-03: "em funcionario não pode ver os grupos que foi criado em
  // permissão [pros outros papéis]".
  // Motorista (2026-10-08) tem a própria gaveta, igual Funcionário.
  const visibleRoles = gpsDriveRoles.filter(
    (r) =>
      (accountType === "funcionario" || accountType === "motorista"
        ? r.scope === accountType
        : r.scope !== "funcionario" && r.scope !== "motorista") &&
      (isFullAdmin || r.name !== "Administrator"),
  );

  // Tela de Permissões totalmente diferente pro Grupo do usuário Funcionário
  // (pedido 2026-09-03) — só as categorias operacionais sobre os clientes,
  // nunca o catálogo inteiro que Admin/Usuário veem (ver
  // FUNCIONARIO_CATEGORY_KEYS). Usado tanto na aba PERMISSÕES (accountType
  // do usuário sendo editado) quanto no painel Criar/Editar Grupo
  // (groupFormScope do Grupo sendo criado/editado, que pode divergir do
  // accountType atual ao editar um Grupo já existente).
  const permissionCategoriesForAccountType = (type: AccountType | RoleScope) =>
    type === "motorista"
      ? MOTORISTA_PERMISSION_CATEGORIES
      : (type === "funcionario"
      ? PERMISSION_CATEGORIES.filter((c) => FUNCIONARIO_CATEGORY_KEYS.includes(c.key))
      : PERMISSION_CATEGORIES
    )
      .filter((c) => isFullAdmin || c.key !== "administrador")
      // Financeiro (2026-09-28) só se configura pra funcionário: admin
      // sempre tem acesso total, cliente nunca tem (ver finance.controller).
      .filter((c) => type === "funcionario" || c.key !== "financeiro")
      // Vendas (2026-10-05): mesmo critério do Financeiro.
      .filter((c) => type === "funcionario" || c.key !== "vendas")
      // Assistência/guinchos (2026-10-16): mesmo critério do Financeiro.
      .filter((c) => type === "funcionario" || c.key !== "central_assistencia")
      // Menus do app/Vistoria (2026-10-08): só fazem sentido pro Motorista
      // (Vistoria "ver de todos" também pro Funcionário, já no filtro acima).
      .filter((c) => c.key !== "menus_app" && (type === "funcionario" || type === "usuario" || c.key !== "vistoria"))
      // Cliente (pedido 2026-10-21, cliente AGV): só "Fazer vistoria do
      // veículo" (dos veículos dele, item no Perfil do app); "ver de todos"
      // é só da equipe.
      .map((c) => (type === "usuario" && c.key === "vistoria" ? { ...c, actions: c.actions.filter((a) => a.key === "create") } : c));
  const userPermissionCategories = permissionCategoriesForAccountType(accountType);
  const groupPermissionCategories = permissionCategoriesForAccountType(groupFormScope);

  // Abas CLIENTE/FATURAMENTO/DOCUMENTOS/CONTRATO (pedido 2026-09-03: "a
  // empresa pode marcar o que o funcionário pode ver, fazer") — quem está
  // com este dialog aberto, não o usuário sendo editado. "view" decide se
  // a aba aparece na lista abaixo; as demais só desabilitam os botões que
  // mexem em dado dentro dela (a aba continua visível pra quem só tem
  // "view", só sem os controles de escrita). Só enforcement de frontend
  // por enquanto — mesma situação de Geocercas/Comandos/Relatórios (ver
  // comentário em permission-catalog.constants.ts/permission.guard.ts no
  // backend).
  //
  // `isTargetFuncionario` some dessas 4 categorias independente de
  // permissão — CORRIGIDO 2026-09-03 (rodada anterior tinha isso trocado:
  // escondia baseado em quem estava EDITANDO, não em quem estava sendo
  // editado, e ainda tirou Cliente/Faturamento/Contrato do catálogo do
  // Funcionário por engano — ver FUNCIONARIO_CATEGORY_KEYS). O certo: um
  // Funcionário como EDITOR continua podendo ter essas 4 permissões
  // normalmente pra atender clientes de verdade; o que não faz sentido é a
  // aba aparecer quando o USUÁRIO SENDO EDITADO é ele próprio um
  // Funcionário, já que empregado não tem perfil de cliente/financeiro/
  // contrato/documentos próprio.
  // Motorista (2026-10-08) também não tem perfil de cliente/contrato próprio.
  const isTargetFuncionario = accountType === "funcionario" || accountType === "motorista";
  const canViewCliente = usePermission("cliente", "view") && !isTargetFuncionario;
  const canEditCliente = usePermission("cliente", "edit") && !isTargetFuncionario;
  const canViewFaturamento = usePermission("faturamento", "view") && !isTargetFuncionario;
  const canCreateFaturamento = usePermission("faturamento", "create") && !isTargetFuncionario;
  const canEditFaturamento = usePermission("faturamento", "edit") && !isTargetFuncionario;
  const canDeleteFaturamento = usePermission("faturamento", "delete") && !isTargetFuncionario;
  const canViewDocumentos = usePermission("documentos", "view") && !isTargetFuncionario;
  const canCreateDocumentos = usePermission("documentos", "create") && !isTargetFuncionario;
  const canDeleteDocumentos = usePermission("documentos", "delete") && !isTargetFuncionario;
  const canViewContrato = usePermission("contrato", "view") && !isTargetFuncionario;
  const canEditContrato = usePermission("contrato", "edit") && !isTargetFuncionario;
  // Aba USUÁRIOS (pedido 2026-09-11) — gerir os sub-usuários que este
  // Cliente criou usa as MESMAS permissões granulares "Usuários: editar/
  // excluir" (categoria já existente, sem categoria própria pra isto).
  const canEditSubUsers = usePermission("users", "edit");
  const canDeleteSubUsers = usePermission("users", "delete");

  const handleToggleCategory = (category: string, enabled: boolean) => {
    setEditablePermissions((prev) =>
      prev.map((row) => (row.category === category ? withImpliedView(row, enabled) : row)),
    );
    // "administrador" (lista de permissões) é agora o único controle de
    // acesso total da tela — mantém em sincronia o campo bruto `administrator`
    // do Traccar (antigo toggle solto no topo, removido 2026-08-27), pra que
    // salvar a aba Principal também reflita o mesmo valor.
    if (category === "administrador") setAdministrador(enabled);
  };
  const handleToggleAction = (category: string, action: string, enabled: boolean) =>
    setEditablePermissions((prev) =>
      prev.map((row) =>
        row.category === category
          ? {
              ...row,
              actions: row.actions.map((a) => (a.action === action ? { ...a, enabled } : a)),
            }
          : row,
      ),
    );
  // Devolvem true/false pra handleSaveEdit (abaixo) saber se pode seguir.
  const handleSavePermissions = async (): Promise<boolean> => {
    if (!identity) {
      toast.error("Não foi possível salvar as permissões", {
        description: "Os dados do usuário ainda não terminaram de carregar. Aguarde e tente de novo.",
      });
      return false;
    }
    try {
      await updateUserPermissions.mutateAsync({ userId: identity.id, rows: editablePermissions });
      return true;
    } catch (err) {
      toast.error("Não foi possível salvar as permissões", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
      setTab("permissoes");
      return false;
    }
  };
  const handleSaveClient = async (): Promise<boolean> => {
    if (!identity) {
      toast.error("Não foi possível salvar os dados do cliente", {
        description: "Os dados do usuário ainda não terminaram de carregar. Aguarde e tente de novo.",
      });
      return false;
    }
    // Ver comentário equivalente em handleSave — mesmo motivo, aplicado
    // aqui pro fluxo de editar um cliente já existente.
    if (!cli.celular.trim()) {
      toast.error("Informe o celular do cliente", {
        description: "Obrigatório — é o número usado para avisos por WhatsApp (ex.: Falha de Comunicação).",
      });
      setTab("cliente");
      return false;
    }
    const normalizedContacts = normalizeAuthorizedContacts(authContacts);
    if (normalizedContacts.error) {
      toast.error(normalizedContacts.error);
      setTab("cliente");
      return false;
    }
    try {
      await updateClientProfile.mutateAsync({
        userId: identity.id,
        data: { ...cliStateToClientProfilePayload(cli), authorizedContacts: normalizedContacts.contacts },
      });
      return true;
    } catch (err) {
      toast.error("Não foi possível salvar os dados do cliente", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
      setTab("cliente");
      return false;
    }
  };
  // Selecionar um Grupo já aplica as permissões dele na hora (decisão
  // 2026-08-27 — como "permissões favoritas": escolher o Grupo é o próprio
  // ato de aplicar seu conjunto de permissões, substitui o antigo botão
  // separado "Restaurar permissões padrão do grupo"). Encadeia as duas
  // mutations que já existiam separadas.
  const handleChangeRole = async (roleId: string) => {
    if (!identity) return;
    const previous = pendingRoleId;
    setPendingRoleId(roleId);
    try {
      await changeUserRole.mutateAsync({ userId: identity.id, roleId });
      const restored = await restoreDefaultPermissions.mutateAsync(identity.id);
      setEditablePermissions(restored);
      await queryClient.invalidateQueries({ queryKey: queryKeys.gpsDriveUserIdentity(editId ?? 0) });
      toast.success("Grupo do usuário atualizado", {
        description: "Permissões do usuário aplicadas com o padrão do Grupo.",
      });
    } catch (err) {
      setPendingRoleId(previous);
      toast.error("Não foi possível trocar o Grupo", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };

  // "Grupo do usuário" (pedido 2026-09-03) — independente da troca de
  // Grupo de permissão acima: nunca mexe em roleId/permissões, só decide
  // qual "gaveta" de Grupos de permissão aparece (ver visibleRoles). Limpa
  // o Grupo de permissão selecionado ao trocar de papel — o antigo pode
  // não pertencer mais à gaveta visível, força reescolha explícita.
  const handleChangeAccountType = async (value: string) => {
    const next = value as AccountType;
    // "revenda" nunca é aceito por PATCH /account-type (ver
    // MUTABLE_ACCOUNT_TYPES no backend) — accountTypeOptions já esconde a
    // opção quando persisted, isto é só defesa em profundidade.
    if (persisted && identity && next !== "revenda") {
      const previous = accountType;
      setAccountType(next);
      setPendingRoleId("");
      try {
        await changeAccountType.mutateAsync({ userId: identity.id, accountType: next });
        await queryClient.invalidateQueries({ queryKey: queryKeys.gpsDriveUserIdentity(editId ?? 0) });
        toast.success("Grupo do usuário atualizado");
      } catch (err) {
        setAccountType(previous);
        toast.error("Não foi possível trocar o Grupo do usuário", {
          description: err instanceof GpsDriveApiError ? err.message : undefined,
        });
      }
    } else {
      setAccountType(next);
      setPendingRoleId("");
      // Usuário novo virando Motorista: já parte de um conjunto que faz
      // sentido (mapa/veículos/eventos/histórico + vistoria), se o operador
      // ainda não marcou nada à mão.
      if (
        next === "motorista" &&
        !editablePermissions.some((c) => c.enabled || c.actions.some((a) => a.enabled))
      ) {
        setEditablePermissions(motoristaDefaultPermissions());
      }
      if (next === "funcionario") setEditablePermissions((prev) => withFuncionarioDefaults(prev));
    }
  };

  const handleToggleNewGroupCategory = (category: string, enabled: boolean) =>
    setNewGroupPermissions((prev) =>
      prev.map((row) => (row.category === category ? withImpliedView(row, enabled) : row)),
    );
  const handleToggleNewGroupAction = (category: string, action: string, enabled: boolean) =>
    setNewGroupPermissions((prev) =>
      prev.map((row) =>
        row.category === category
          ? {
              ...row,
              actions: row.actions.map((a) => (a.action === action ? { ...a, enabled } : a)),
            }
          : row,
      ),
    );
  const openCreateGroup = () => {
    setEditingGroupId(null);
    setNewGroupName("");
    setNewGroupPermissions(blankPermissions());
    // Grupo criado agora nasce na mesma "gaveta" do accountType atual —
    // nunca escolhido à mão (pedido 2026-09-03: "quando escolher o
    // funcionario pode criar um grupo para salvar as preferencias de
    // permissão para o funcionario").
    setGroupFormScope(
      accountType === "funcionario" || accountType === "motorista" ? accountType : "geral",
    );
    setCreateGroupOpen(true);
    setTab("principal");
  };
  // "Editar grupo" (pedido 2026-09-03) — pré-carrega nome+permissões reais
  // do Grupo via GET /roles/:id (só existe desde este pedido, ver
  // gpsdrive-roles.service.ts#get): antes não tinha como ler o conteúdo de
  // um Grupo sem primeiro aplicá-lo a um usuário de verdade.
  const openEditGroup = async (roleId: string) => {
    setLoadingGroupDetail(true);
    try {
      const detail = await gpsDriveRolesService.get(roleId);
      setEditingGroupId(detail.id);
      setNewGroupName(detail.name);
      setNewGroupPermissions(detail.permissions);
      setGroupFormScope(detail.scope);
      setCreateGroupOpen(true);
      setTab("principal");
    } catch (err) {
      toast.error("Não foi possível carregar o grupo", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    } finally {
      setLoadingGroupDetail(false);
    }
  };
  const handleSaveGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Dê um nome ao grupo");
      return;
    }
    try {
      if (editingGroupId) {
        await updateRole.mutateAsync({
          id: editingGroupId,
          name: newGroupName.trim(),
          scope: groupFormScope,
          permissions: newGroupPermissions,
        });
        toast.success("Grupo atualizado", {
          description: "Usuários que já estão neste Grupo não mudam sozinhos — a nova versão só vale para quem selecionar o Grupo a partir de agora.",
        });
      } else {
        await createRole.mutateAsync({
          name: newGroupName.trim(),
          scope: groupFormScope,
          permissions: newGroupPermissions,
        });
        toast.success("Grupo criado", { description: `"${newGroupName.trim()}" já aparece no select Grupo.` });
      }
      setCreateGroupOpen(false);
      setEditingGroupId(null);
    } catch (err) {
      toast.error(editingGroupId ? "Não foi possível salvar o grupo" : "Não foi possível criar o grupo", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
    }
  };
  const handleDeleteGroup = async () => {
    if (!pendingRoleId) return;
    try {
      await deleteRole.mutateAsync(pendingRoleId);
      toast.success("Grupo excluído");
      setPendingRoleId("");
      setDeleteGroupConfirmOpen(false);
    } catch (err) {
      toast.error("Não foi possível excluir o grupo", {
        description: err instanceof GpsDriveApiError ? err.message : undefined,
      });
      setDeleteGroupConfirmOpen(false);
    }
  };

  const reset = () => {
    setCreatedUser(null);
    setResellerCompanyName("");
    setTab("principal");
    setNome("");
    setLoginEmail("");
    setTelefone("");
    setAdministrador(false);
    setSomenteLeitura(false);
    setSomenteLeituraDispositivos(false);
    setDesabilitado(false);
    setLimiteDispEnabled(false);
    setLimiteDisp("");
    setLimiteUsuariosEnabled(false);
    setLimiteUsuarios("");
    setValidadeEnabled(false);
    setValidade("");
    // Senha padrão sugerida ao cadastrar um usuário novo — o operador pode
    // trocar antes de salvar, mas o campo não fica vazio por padrão.
    setSenha("123456");
    setConfSenha("123456");
    setShowSenha(false);
    setShowConfSenha(false);
    setCli({
      cpfCnpj: "", razaoSocial: "", nomeContrato: "", rg: "",
      dataNascimento: "", cep: "", endereco: "", numero: "",
      complemento: "", bairro: "", cidade: "", estado: "",
      celular: "", valorMensalidade: "", foneFixo: "", diaVencimento: "",
      emailCobranca: "", rendaFaturamento: "", numeroSuporte: "", notas: "",
    });
    setAuthContacts([]);
    setVincSelected({});
    setVincQuery("");
    setEditablePermissions(blankPermissions());
    setPendingRoleId("");
    setAccountType("usuario");
    setInvoicesOffset(0);
    setShowNewInvoiceForm(false);
    setNewInvoice({ value: "", dueDate: "", billingType: "PIX", description: "" });
    setShowRecurrenceDialog(false);
    setVehiclePlans({});
  };

  // Pré-preenche campos com o usuário real do Traccar (GET /api/users/{id})
  // ao abrir em modo de edição.
  useEffect(() => {
    if (!open) return;
    if (!persisted) {
      reset();
      if (prefill) {
        setNome(prefill.nome);
        setLoginEmail(prefill.loginEmail);
        setTelefone(prefill.telefone);
        setCli((prev) => ({ ...prev, ...prefill.cli }));
      }
      return;
    }
    if (!freshUser) return;
    setNome(freshUser.name ?? "");
    setLoginEmail(freshUser.email ?? "");
    setTelefone(freshUser.phone ?? "");
    setAdministrador(!!freshUser.administrator);
    setSomenteLeitura(!!freshUser.readonly);
    setSomenteLeituraDispositivos(!!freshUser.deviceReadonly);
    setDesabilitado(!!freshUser.disabled);
    const dl = freshUser.deviceLimit;
    setLimiteDispEnabled(dl != null && dl >= 0);
    setLimiteDisp(dl != null && dl >= 0 ? String(dl) : "");
    const ul = freshUser.userLimit;
    setLimiteUsuariosEnabled(ul != null && ul > 0);
    setLimiteUsuarios(ul != null && ul > 0 ? String(ul) : "");
    setValidadeEnabled(!!freshUser.expirationTime);
    setValidade(freshUser.expirationTime ? freshUser.expirationTime.slice(0, 10) : "");
    setSenha("");
    setConfSenha("");
  }, [open, persisted, freshUser, prefill]);

  // Marca automaticamente os checkboxes dos veículos já vinculados ao
  // usuário (estado atual real, vindo de GET /api/devices?userId=).
  useEffect(() => {
    if (!open || !persisted) return;
    if (!linkedDeviceIds) return;
    setVincSelected(Object.fromEntries(linkedDeviceIds.map((id) => [id, true])));
  }, [open, persisted, linkedDeviceIds]);

  const selectAllFiltered = () =>
    setVincSelected((p) => ({
      ...p,
      ...Object.fromEntries(filteredDevices.map((v) => [v.id, true])),
    }));
  const clearSelection = () => setVincSelected({});

  // Cobre só o trecho de handleSave que roda por FORA das mutations de cima
  // (resolver a identidade recém-criada + aplicar Permissões/Cliente) — as
  // próprias mutations de criar/atualizar usuário e sincronizar veículos já
  // entram em `saving` acima.
  const [savingExtras, setSavingExtras] = useState(false);
  const saving = createUser.isPending || updateUser.isPending || syncDevices.isPending || savingExtras;

  const handleSave = async () => {
    if (!nome.trim() || !loginEmail.trim()) {
      toast.error("Preencha os campos obrigatórios", {
        description: "Nome e Email/Login são obrigatórios na aba Principal.",
      });
      setTab("principal");
      return;
    }
    if ((senha || confSenha) && senha !== confSenha) {
      toast.error("As senhas não coincidem", {
        description: "Confira o campo de confirmação de senha.",
      });
      setTab("principal");
      return;
    }
    if (!persisted && !senha) {
      toast.error("Informe uma senha", {
        description: "Senha é obrigatória ao criar um novo usuário.",
      });
      setTab("principal");
      return;
    }
    if (!persisted && accountType === "revenda" && !resellerCompanyName.trim()) {
      toast.error("Informe o nome da empresa da revenda", {
        description: "Necessário para criar a empresa filha desta revenda.",
      });
      setTab("principal");
      return;
    }
    // Celular do cliente é obrigatório assim que qualquer dado da aba
    // Cliente é preenchido (pedido 2026-09-10) — é o único jeito de
    // Falha de Comunicação (Administração > Comunicação) conseguir avisar
    // esse cliente por WhatsApp quando o veículo dele ficar offline.
    if (!persisted && Object.values(cli).some((v) => v.trim() !== "") && !cli.celular.trim()) {
      toast.error("Informe o celular do cliente", {
        description: "Obrigatório na aba Cliente — é o número usado para avisos por WhatsApp.",
      });
      setTab("cliente");
      return;
    }
    if (!persisted) {
      const contactsCheck = normalizeAuthorizedContacts(authContacts);
      if (contactsCheck.error) {
        toast.error(contactsCheck.error);
        setTab("cliente");
        return;
      }
    }

    const payload: Partial<User> = {
      name: nome.trim(),
      email: loginEmail.trim(),
      phone: telefone.trim() || undefined,
      // Funcionário (pedido 2026-09-23) precisa do administrator do Traccar
      // pra enxergar os clientes/veículos da empresa e cadastrar usuário lá
      // (sem isso o Traccar recusa POST /api/users: userLimit 0) — o que
      // ele pode fazer DENTRO do app continua decidido só pelas permissões
      // granulares (a categoria "administrador" nem aparece pra Funcionário).
      administrator: canGrantTraccarAdmin
        ? administrador || accountType === "funcionario"
        : persisted && !!freshUser?.administrator,
      readonly: somenteLeitura,
      // Cliente só renomeia dispositivo (2026-09-24): deviceReadonly do
      // Traccar = (Grupo do usuário "usuario") — o backend força a mesma
      // regra no login e ao trocar o Grupo (client-device-readonly.ts),
      // aqui só evita este PUT desfazer.
      deviceReadonly: isRestrictedAccountType(accountType),
      disabled: desabilitado,
      // Gerente do Traccar (admin de empresa, fase 4 2026-10-17) não pode
      // mexer em deviceLimit/userLimit — o Traccar recusa o POST/PUT inteiro
      // ("Administrator access required"). Cria com 0/0 e, na edição, mantém
      // o que já está lá; admin/funcionário ganha -1/-1 pelo backend (conta
      // de serviço) ao trocar o Grupo do usuário — ver
      // client-traccar-restrictions.ts no gpsdrive-backend.
      ...(canGrantTraccarAdmin
        ? {
            deviceLimit: limiteDispEnabled ? Number(limiteDisp) || 0 : -1,
            // -1 (gerente sem limite, sem campo próprio na tela) não pode
            // virar 0 só por ter salvo com o switch desligado.
            userLimit: limiteUsuariosEnabled
              ? Number(limiteUsuarios) || 0
              : persisted && freshUser?.userLimit === -1
                ? -1
                : 0,
          }
        : {
            deviceLimit: persisted ? (freshUser?.deviceLimit ?? 0) : 0,
            userLimit: persisted ? (freshUser?.userLimit ?? 0) : 0,
          }),
      expirationTime: validadeEnabled && validade ? new Date(validade).toISOString() : null,
    };
    if (senha) payload.password = senha;

    // Capturado antes da mutation: é a criação em si (primeiro Salvar deste
    // dialog aberto em modo "novo usuário") que decide a mensagem/o que
    // acontece depois — depois de setCreatedUser, `persisted` já vira true e
    // não serviria mais pra diferenciar "acabei de criar" de "já existia".
    const creatingNow = !persisted;

    try {
      const savedUser = persisted
        ? await updateUser.mutateAsync({
            id: editId!,
            data: { ...(freshUser ?? initial?.user ?? createdUser), ...payload },
          })
        : await createUser.mutateAsync(payload);

      // O diff entre o vínculo atual e o que está marcado na tela agora é
      // calculado no GPS Drive Backend (ver useSyncUserDevicePermissions) —
      // aqui só decidimos SE mudou algo, pra pular a chamada e ajustar o
      // texto do toast quando não há nada a sincronizar.
      const baseline = new Set(persisted ? (linkedDeviceIds ?? []) : []);
      const selected = new Set(
        Object.entries(vincSelected)
          .filter(([, v]) => v)
          .map(([id]) => id),
      );
      const changed =
        selected.size !== baseline.size || [...selected].some((id) => !baseline.has(id));
      if (changed) {
        // Tratado à parte do catch de fora (ver bloco de Permissões/Cliente
        // logo abaixo, mesmo motivo): se isto falhar, o usuário JÁ foi
        // criado com sucesso no passo anterior — o catch genérico lá
        // embaixo diz "Não foi possível criar o usuário", o que é falso e
        // confunde o operador (bug real reportado 2026-08-31: viu esse
        // erro mesmo com o usuário aparecendo certinho na lista logo
        // depois).
        try {
          await syncDevices.mutateAsync({ userId: savedUser.id, deviceIds: [...selected] });
        } catch (err) {
          if (creatingNow) onCreated?.(savedUser);
          toast.error("Usuário criado, mas houve um problema ao vincular os veículos", {
            description:
              (err instanceof GpsDriveApiError ? err.message : "Tente de novo pela aba Veículos.") +
              " O usuário já foi criado normalmente.",
          });
          setCreatedUser(savedUser);
          setTab("veiculos");
          return;
        }
      }

      // Usuário novo: já aplica o que foi preenchido em Permissões/Cliente
      // NESTA MESMA sessão de criação — pedido explícito do operador
      // 2026-08-31, o fluxo anterior (que só liberava essas abas DEPOIS do
      // primeiro Salvar) ainda obrigava passar pelo "Salve o usuário
      // primeiro" pra tudo. Uma falha aqui não desfaz a criação do usuário
      // (o Traccar já criou) — mantém o dialog aberto "virando" edição pra
      // o operador corrigir/tentar de novo pelas próprias abas, já
      // destravadas.
      let permissionsApplied = false;
      let clientApplied = false;
      // Revenda (pedido 2026-09-07) — fluxo próprio, nunca passa por
      // resolveByTraccarId/changeAccountType abaixo: createReseller já cria
      // a empresa filha E provisiona este exato traccarUserId nela como
      // Administrator (role + permissão "administrador" bypass-all), tudo
      // no mesmo passo no backend (ver CompaniesService#createReseller).
      // Chamar resolveByTraccarId antes disso provisionaria o usuário na
      // empresa do OPERADOR por engano — por isso o branch totalmente
      // separado, nunca reaproveitando o bloco de baixo.
      if (creatingNow && accountType === "revenda") {
        setSavingExtras(true);
        try {
          await createReseller.mutateAsync({
            traccarUserId: savedUser.id,
            companyName: resellerCompanyName.trim(),
          });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.gpsDriveUserIdentity(savedUser.id),
          });
        } catch (err) {
          toast.error("Usuário criado, mas houve um problema ao criar a empresa da revenda", {
            description:
              (err instanceof GpsDriveApiError ? err.message : "Tente de novo pela aba Principal.") +
              " O login já existe no Traccar — exclua-o antes de tentar de novo, para não ficar órfão.",
          });
          setCreatedUser(savedUser);
          setTab("principal");
          return;
        } finally {
          setSavingExtras(false);
        }
      } else if (creatingNow) {
        const hasCustomPermissions = editablePermissions.some(
          (c) => c.enabled || c.actions.some((a) => a.enabled),
        );
        const newContacts = normalizeAuthorizedContacts(authContacts).contacts;
        const hasClientData = Object.values(cli).some((v) => v.trim() !== "") || newContacts.length > 0;
        // accountType default do backend já é "usuario" — só chama o
        // endpoint se o operador escolheu outro papel na criação. Nunca
        // "revenda" aqui (esse caso já foi tratado no branch acima, que
        // sempre entra primeiro quando accountType === "revenda").
        const needsAccountType = accountType !== "usuario" && accountType !== "revenda";
        if (pendingRoleId || hasCustomPermissions || hasClientData || needsAccountType) {
          setSavingExtras(true);
          try {
            const newIdentity = await gpsDriveUsersService.resolveByTraccarId(savedUser.id);
            if (needsAccountType) {
              await changeAccountType.mutateAsync({ userId: newIdentity.id, accountType });
            }
            // Grupo escolhido na criação (ver bloco Grupo acima) — mesmo
            // endpoint que a troca de Grupo em Editar Usuário, agora que já
            // existe um userId real pra aplicar. Tem precedência sobre
            // toggles manuais no accordion (mutuamente exclusivos na tela —
            // ver condição "(persisted || !pendingRoleId)" que esconde o
            // accordion quando um Grupo está escolhido).
            if (pendingRoleId) {
              await changeUserRole.mutateAsync({ userId: newIdentity.id, roleId: pendingRoleId });
              // Mesma dupla da troca de Grupo em Editar (handleChangeRole):
              // sem isso o usuário nascia com o Grupo mas tudo desativado.
              await restoreDefaultPermissions.mutateAsync(newIdentity.id);
              permissionsApplied = true;
            } else if (hasCustomPermissions) {
              await gpsDriveUserPermissionsService.replace(newIdentity.id, editablePermissions);
              permissionsApplied = true;
            }
            if (hasClientData) {
              await gpsDriveClientProfileService.replace(newIdentity.id, {
                ...cliStateToClientProfilePayload(cli),
                authorizedContacts: newContacts,
              });
              clientApplied = true;
            }
            await queryClient.invalidateQueries({ queryKey: queryKeys.gpsDriveUserIdentity(savedUser.id) });
          } catch (err) {
            if (creatingNow) onCreated?.(savedUser);
            toast.error("Usuário criado, mas houve um problema ao aplicar Permissões/Cliente", {
              description:
                (err instanceof GpsDriveApiError ? err.message : "Tente de novo pelas próprias abas.") +
                " O usuário já foi criado normalmente.",
            });
            setCreatedUser(savedUser);
            setTab(
              !permissionsApplied && (pendingRoleId || needsAccountType)
                ? "principal"
                : !permissionsApplied && hasCustomPermissions
                  ? "permissoes"
                  : "cliente",
            );
            return;
          } finally {
            setSavingExtras(false);
          }
        }
      }

      if (creatingNow) onCreated?.(savedUser);
      toast.success(creatingNow ? "Usuário criado" : "Usuário atualizado", {
        description:
          `${payload.name} foi ${creatingNow ? "adicionado" : "atualizado"}` +
          (changed ? " e os veículos vinculados foram atualizados." : ".") +
          (permissionsApplied ? " Permissões aplicadas." : "") +
          (clientApplied ? " Dados do cliente salvos." : ""),
      });

      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(creatingNow ? "Não foi possível criar o usuário" : "Não foi possível atualizar o usuário", {
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  // Salvar em modo edição (2026-10-01): antes o botão salvava SÓ a aba
  // aberta — mexer na Principal e clicar Salvar estando em Cliente ou
  // Permissões jogava fora o que foi editado na Principal (e vice-versa),
  // e o popup não fechava. Agora grava Cliente/Permissões se mudaram e
  // termina no handleSave (Principal + Veículos), que fecha ao dar certo.
  const [savingEdit, setSavingEdit] = useState(false);
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const cliDirty =
        canEditCliente &&
        (clientProfile !== undefined
          ? JSON.stringify(cli) !== JSON.stringify(clientProfileToCliState(clientProfile)) ||
            !sameAuthorizedContacts(authContacts, clientProfile?.authorizedContacts ?? [])
          : Object.values(cli).some((v) => v.trim() !== "") || authContacts.length > 0);
      if (cliDirty && !(await handleSaveClient())) return;
      const permsDirty =
        canManagePermissions &&
        !!userPermissions &&
        JSON.stringify(editablePermissions) !== JSON.stringify(userPermissions);
      if (permsDirty && !(await handleSavePermissions())) return;
      await handleSave();
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserIcon className="h-4 w-4" />
            {persisted ? "Editar" : "Adicionar novo usuário"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {persisted ? "Editar usuário" : "Cadastro completo de usuário"}
          </DialogDescription>
        </DialogHeader>

        {/* Aba PERMISSÕES/CLIENTE dependem do GPS Drive Backend (identity,
            useUserPermissions, useClientProfile) — sem este banner, uma
            sessão de gestão expirada (ver auth-context.tsx#gpsDriveStatus)
            deixava essas abas presas em "Carregando…" pra sempre, sem
            nenhum jeito de o Administrator perceber o motivo ou se
            recuperar (bug real 2026-08-27: só o dialog de veículo tinha
            este aviso, este dialog não). Mesmo componente/padrão do
            VehicleFormPanel — reautentica só o GPS Drive Backend, nunca
            desloga do Traccar. */}
        <GpsDriveSessionBanner />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="border-b border-border px-6 pt-3">
            <TabsList className="h-auto gap-1 bg-transparent p-0">
              {[
                { v: "principal", l: "PRINCIPAL" },
                { v: "permissoes", l: "PERMISSÕES" },
                ...(canViewCliente ? [{ v: "cliente", l: "CLIENTE" }] : []),
                ...(persisted
                  ? [
                      ...(canViewFaturamento ? [{ v: "faturamento", l: "FATURAMENTO" }] : []),
                      ...(canViewDocumentos ? [{ v: "documentos", l: "DOCUMENTOS" }] : []),
                      ...(canViewContrato ? [{ v: "contrato", l: "CONTRATO" }] : []),
                      { v: "logs", l: "LOGS" },
                    ]
                  : []),
                { v: "veiculos", l: "VEÍCULOS" },
                // Sem categoria de permissão própria (nunca teve) — mesmo
                // critério das 4 abas acima: some quando o usuário SENDO
                // EDITADO é um Funcionário (não tem sessões/dispositivo de
                // cliente pra mostrar), não quando quem edita é Funcionário.
                ...(persisted && !isTargetFuncionario
                  ? [{ v: "dispositivos", l: "DISPOSITIVOS CONECTADOS" }]
                  : []),
                // Sub-usuários (pedido 2026-09-10) — só existe pra quem
                // pode TER sub-usuários: um Cliente persistido (accountType
                // "usuario"). Nunca aparece em modo Cadastrar (ninguém
                // ainda criou nada) nem pra Admin/Funcionário/Revenda
                // (eles usam a tela Usuários inteira, não este mecanismo).
                ...(persisted && accountType === "usuario"
                  ? [{ v: "usuarios", l: "USUÁRIOS" }]
                  : []),
              ].map((t) => (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  className="rounded-md px-3 py-1.5 text-[11px] font-semibold tracking-wider data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground"
                >
                  {t.l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* PRINCIPAL — integração real com o Traccar (GET/POST/PUT /api/users) */}
          <TabsContent value="principal" className="px-6 py-5">
            {/* Grupo — pedido 2026-09-03: "não quero que o grupo fique
                dentro de permissões, quero que fique em cima do nome" +
                "eu possa editar ou excluir algum grupo". Disponível tanto
                pra criar quanto editar usuário (ver onValueChange abaixo);
                Editar/Excluir grupo agem sobre o Grupo em si (catálogo da
                empresa), não sobre o usuário deste dialog — por isso
                aparecem sempre que um Grupo está selecionado, não só
                quando persisted. */}
            {createGroupOpen ? (
              // Painel inline, NUNCA um <Dialog> aninhado dentro deste
              // dialog (Editar Usuário) — testado 2026-08-27: dois Dialog
              // do Radix empilhados quebravam a interação com os Switches
              // do accordion de dentro. Mesma aba, troca de conteúdo só.
              <div className="rounded-lg border border-border p-4">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-sm font-semibold">
                    {editingGroupId ? `Editar grupo "${newGroupName}"` : "Criar grupo"}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCreateGroupOpen(false);
                      setEditingGroupId(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
                <div className="mb-4 text-xs text-muted-foreground">
                  {editingGroupId
                    ? "Altere o nome e/ou as permissões deste Grupo — usuários que já estão neste Grupo não mudam sozinhos, só quem selecionar o Grupo a partir de agora."
                    : "Dê um nome e escolha as permissões deste Grupo — elas ficam salvas como o padrão aplicado toda vez que alguém selecionar este Grupo num usuário."}
                </div>
                <FieldLabel label="Nome do grupo" required>
                  <Input
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Ex.: Funcionário"
                    className="h-10"
                  />
                </FieldLabel>
                <div className="mb-2 mt-4 text-xs font-medium text-muted-foreground">Permissões</div>
                <UserPermissionsAccordion
                  values={newGroupPermissions}
                  onToggleCategory={handleToggleNewGroupCategory}
                  onToggleAction={handleToggleNewGroupAction}
                  categories={groupPermissionCategories}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCreateGroupOpen(false);
                      setEditingGroupId(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveGroup}
                    disabled={createRole.isPending || updateRole.isPending}
                  >
                    {createRole.isPending || updateRole.isPending
                      ? "Salvando…"
                      : editingGroupId
                        ? "Salvar grupo"
                        : "Criar grupo"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Papel — pedido 2026-09-03: "grupo do usuário tem que ter
                    3 grupo[s] admin, usuario, funcionario" — SEPARADO do
                    Grupo de permissão logo abaixo. Só decide qual "gaveta"
                    de Grupos de permissão aparece pro select (ver
                    visibleRoles) — nunca concede nenhuma permissão
                    sozinho. */}
                <div className="mb-4">
                  <FieldLabel label="Grupo do usuário" required>
                    <Select
                      value={accountType}
                      onValueChange={handleChangeAccountType}
                      disabled={!canManagePermissions || changeAccountType.isPending}
                    >
                      <SelectTrigger className="h-9 w-56">
                        <SelectValue placeholder="Selecione o papel" />
                      </SelectTrigger>
                      <SelectContent>
                        {accountTypeOptions.map((t) => (
                          <SelectItem key={t} value={t}>
                            {ACCOUNT_TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldLabel>
                </div>
                {/* Motorista (pedido 2026-10-08: "não apareceu para vincular
                    veículo no motorista") — sem veículo vinculado ele não vê
                    nada no app nem consegue fazer vistoria; o atalho deixa
                    isso explícito logo abaixo do Grupo do usuário. */}
                {accountType === "motorista" && (
                  <div
                    className={cn(
                      "mb-4 flex flex-wrap items-center gap-3 rounded-lg border p-3",
                      vincCount === 0
                        ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30"
                        : "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30",
                    )}
                  >
                    <Car className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1 text-xs">
                      <div className="font-semibold text-foreground">Veículo do motorista</div>
                      <div className="text-muted-foreground">
                        {vincCount === 0
                          ? "Nenhum veículo vinculado — o motorista não verá nada no app nem conseguirá fazer vistoria."
                          : `${vincCount} veículo${vincCount === 1 ? "" : "s"} vinculado${vincCount === 1 ? "" : "s"}.`}
                      </div>
                    </div>
                    <Button size="sm" variant={vincCount === 0 ? "default" : "outline"} onClick={() => setTab("veiculos")}>
                      {vincCount === 0 ? "Vincular veículo" : "Alterar veículos"}
                    </Button>
                  </div>
                )}
                {/* Revenda (pedido 2026-09-07): campo extra só na criação —
                    o nome vira uma empresa nova, filha da empresa atual,
                    com faturamento próprio. Nunca editável depois (ver
                    accountTypeOptions acima, que já esconde a opção). */}
                {!persisted && accountType === "revenda" && (
                  <div className="mb-4">
                    <FieldLabel label="Nome da empresa (revenda)" required>
                      <Input
                        value={resellerCompanyName}
                        onChange={(e) => setResellerCompanyName(e.target.value)}
                        placeholder="Ex.: Rastreamento Fulano LTDA"
                        className="h-9 w-72"
                      />
                    </FieldLabel>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Cria uma empresa separada, com faturamento próprio e as mesmas
                      permissões de Administrador — seus veículos aparecerão aqui com uma
                      etiqueta "Revenda", mas esta empresa nunca verá os seus dados.
                    </p>
                  </div>
                )}
                <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                  <FieldLabel label="Grupo de permissão">
                    <Select
                      value={pendingRoleId}
                      onValueChange={persisted ? handleChangeRole : setPendingRoleId}
                      disabled={
                        !canManagePermissions || changeUserRole.isPending || restoreDefaultPermissions.isPending
                      }
                    >
                      <SelectTrigger className="h-9 w-56">
                        <SelectValue placeholder="Selecione um grupo" />
                      </SelectTrigger>
                      <SelectContent>
                        {visibleRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldLabel>
                  {canManagePermissions && (
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={openCreateGroup}>
                        Criar grupo
                      </Button>
                      {pendingRoleId && gpsDriveRoles.find((r) => r.id === pendingRoleId)?.name !== "Administrator" && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openEditGroup(pendingRoleId)}
                            disabled={loadingGroupDetail}
                          >
                            {loadingGroupDetail ? "Carregando…" : "Editar grupo"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteGroupConfirmOpen(true)}
                          >
                            Excluir grupo
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {!persisted && pendingRoleId && (
                  <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300">
                    As permissões do Grupo "{gpsDriveRoles.find((r) => r.id === pendingRoleId)?.name}" serão aplicadas
                    a este usuário ao salvar (aba PERMISSÕES).{" "}
                    <button type="button" className="font-semibold underline" onClick={() => setPendingRoleId("")}>
                      Personalizar manualmente em vez disso
                    </button>
                  </div>
                )}

                {persisted && (
              <div className="mb-6 flex items-center gap-5">
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    void handlePickAvatar(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  aria-label="Trocar imagem do usuário"
                  title="Trocar imagem do usuário"
                  disabled={avatarUploading}
                  onClick={() => avatarInputRef.current?.click()}
                  className="group/avatar relative flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border bg-muted/40 transition hover:border-primary/50"
                >
                  {displayedAvatarUrl && !avatarFailed ? (
                    <img
                      src={displayedAvatarUrl}
                      alt={initial?.name ?? nome}
                      className="h-full w-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <UserIcon className="h-10 w-10 text-muted-foreground" />
                  )}
                  <span
                    aria-hidden
                    className={cn(
                      "absolute inset-0 flex items-center justify-center bg-black/55 text-white opacity-0 transition-opacity group-hover/avatar:opacity-100",
                      avatarUploading && "opacity-100",
                    )}
                  >
                    {avatarUploading ? (
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      <Camera className="h-6 w-6" strokeWidth={2.25} />
                    )}
                  </span>
                </button>
                <div className="space-y-1.5">
                  <div className="text-base font-bold uppercase text-foreground">{initial?.name ?? nome}</div>
                  <div className="text-xs text-muted-foreground">ID: {editId}</div>
                  {loadingUser && (
                    <div className="text-xs text-muted-foreground">Carregando dados do usuário…</div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FieldLabel label="Nome:" required>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} className="h-10" />
              </FieldLabel>
              <FieldLabel label="Email/Login:" required>
                <Input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="h-10" />
              </FieldLabel>
              <FieldLabel label="Telefone:">
                <Input
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  value={telefone}
                  onChange={(e) => setTelefone(onlyDigits(e.target.value))}
                  className="h-10"
                />
              </FieldLabel>
              <div />
              {/* Só administrator do Traccar mexe em limites (ver payload). */}
              {canGrantTraccarAdmin && (
                <>
                  <FieldLabel label="Limite de dispositivos:">
                    <div className="flex items-center gap-2">
                      <Switch checked={limiteDispEnabled} onCheckedChange={setLimiteDispEnabled} />
                      <Input
                        disabled={!limiteDispEnabled}
                        value={limiteDisp}
                        onChange={(e) => setLimiteDisp(e.target.value)}
                        type="number"
                        className="h-10 flex-1"
                      />
                    </div>
                  </FieldLabel>
                  <FieldLabel label="Limite de usuários:">
                    <div className="flex items-center gap-2">
                      <Switch checked={limiteUsuariosEnabled} onCheckedChange={setLimiteUsuariosEnabled} />
                      <Input
                        disabled={!limiteUsuariosEnabled}
                        value={limiteUsuarios}
                        onChange={(e) => setLimiteUsuarios(e.target.value)}
                        type="number"
                        className="h-10 flex-1"
                      />
                    </div>
                  </FieldLabel>
                </>
              )}
              <FieldLabel label="Data de expiração:">
                <div className="flex items-center gap-2">
                  <Switch checked={validadeEnabled} onCheckedChange={setValidadeEnabled} />
                  <Input
                    disabled={!validadeEnabled}
                    value={validade}
                    onChange={(e) => setValidade(e.target.value)}
                    type="date"
                    className="h-10 flex-1"
                  />
                </div>
              </FieldLabel>
              <div />
              <FieldLabel label="Senha:" required={!persisted}>
                <div className="relative">
                  <Input
                    type={showSenha ? "text" : "password"}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder={persisted ? "Deixe em branco para manter a atual" : ""}
                    autoComplete="new-password"
                    className="h-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenha((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FieldLabel>
              <FieldLabel label="Confirmação de Senha:">
                <div className="relative">
                  <Input
                    type={showConfSenha ? "text" : "password"}
                    value={confSenha}
                    onChange={(e) => setConfSenha(e.target.value)}
                    autoComplete="new-password"
                    className="h-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfSenha((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FieldLabel>
            </div>
              </>
            )}
          </TabsContent>

          {/* CLIENTE — Cliente/Financeiro, real desde 2026-08-27 (GPS Drive
              Backend próprio, ver gpsdrive-client-profile.service.ts).
              Editável mesmo num usuário novo ainda não salvo (`cli` é
              estado local, não depende de `identity`) — só é ENVIADO pro
              servidor no handleSave, junto da criação do usuário (ver
              `hasClientData` lá), pra não obrigar salvar duas vezes. */}
          <TabsContent value="cliente" className="px-6 py-5">
            {!canViewCliente ? (
              <p className="mt-6 text-sm text-muted-foreground">Você não tem permissão para ver esta aba.</p>
            ) : (
            <fieldset disabled={!canEditCliente} className="contents">
            <div className="mb-4 flex items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
                <UserIcon className="h-3.5 w-3.5" />
                Dados do Cliente
              </div>
            </div>
            {identityFailed ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                Não foi possível carregar os dados do cliente.{" "}
                <button type="button" className="underline" onClick={() => refetchIdentity()}>
                  Tentar de novo
                </button>
              </div>
            ) : persisted && !identity ? (
              <div className="text-xs text-muted-foreground">Carregando dados do cliente…</div>
            ) : (
            <div className="space-y-3">
              <FieldLabel label="CPF/CNPJ:">
                <Input value={cli.cpfCnpj} onChange={(e) => setCliField("cpfCnpj", e.target.value)} className="h-10" />
              </FieldLabel>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <FieldLabel label="Nome/Razão Social:">
                  <Input value={cli.razaoSocial} onChange={(e) => setCliField("razaoSocial", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Nome para Contrato:">
                  <Input placeholder="Usa a razão social se vazio" value={cli.nomeContrato} onChange={(e) => setCliField("nomeContrato", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="RG:">
                  <Input value={cli.rg} onChange={(e) => setCliField("rg", e.target.value)} className="h-10" />
                </FieldLabel>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FieldLabel label="Data de nascimento:">
                  <Input type="date" value={cli.dataNascimento} onChange={(e) => setCliField("dataNascimento", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="CEP:">
                  <Input
                    value={cli.cep}
                    onChange={(e) => setCliField("cep", e.target.value)}
                    onBlur={async (e) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      if (raw.length !== 8) return;
                      try {
                        const r = await fetch(`https://viacep.com.br/ws/${raw}/json/`);
                        const d = await r.json();
                        if (d && !d.erro) {
                          setCliField("endereco", d.logradouro || "");
                          setCliField("bairro", d.bairro || "");
                          setCliField("cidade", d.localidade || "");
                          setCliField("estado", d.uf || "");
                        }
                      } catch { /* ignore */ }
                    }}
                    placeholder="00000-000"
                    className="h-10"
                  />
                </FieldLabel>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px]">
                <FieldLabel label="Endereço:">
                  <Input value={cli.endereco} onChange={(e) => setCliField("endereco", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Nº:">
                  <Input value={cli.numero} onChange={(e) => setCliField("numero", e.target.value)} className="h-10" />
                </FieldLabel>
              </div>
              <FieldLabel label="Complemento:">
                <Input value={cli.complemento} onChange={(e) => setCliField("complemento", e.target.value)} className="h-10" />
              </FieldLabel>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <FieldLabel label="Bairro:">
                  <Input value={cli.bairro} onChange={(e) => setCliField("bairro", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Cidade:">
                  <Input value={cli.cidade} onChange={(e) => setCliField("cidade", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Estado:">
                  <Input value={cli.estado} onChange={(e) => setCliField("estado", e.target.value)} className="h-10" />
                </FieldLabel>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <FieldLabel label="Celular:" requiredColor>
                  <Input
                    type="tel"
                    autoComplete="tel"
                    inputMode="numeric"
                    value={cli.celular}
                    onChange={(e) => setCliField("celular", onlyDigits(e.target.value))}
                    className="h-10"
                  />
                </FieldLabel>
                <FieldLabel label="Valor mensalidade:">
                  <Input value={cli.valorMensalidade} onChange={(e) => setCliField("valorMensalidade", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Fone fixo:">
                  <Input value={cli.foneFixo} onChange={(e) => setCliField("foneFixo", e.target.value)} className="h-10" />
                </FieldLabel>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <FieldLabel label="Dia de vencimento (Fatura):" requiredColor>
                  <Select value={cli.diaVencimento} onValueChange={(v) => setCliField("diaVencimento", v)}>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldLabel>
                <FieldLabel label="Email de Cobrança:" required>
                  <Input value={cli.emailCobranca} onChange={(e) => setCliField("emailCobranca", e.target.value)} className="h-10" />
                </FieldLabel>
                <FieldLabel label="Renda/Faturamento:">
                  <Input placeholder="R$ 0,00" value={cli.rendaFaturamento} onChange={(e) => setCliField("rendaFaturamento", e.target.value)} className="h-10" />
                </FieldLabel>
              </div>
              <FieldLabel label="Número para retorno de suporte:">
                <Input value={cli.numeroSuporte} onChange={(e) => setCliField("numeroSuporte", e.target.value)} className="h-10" />
              </FieldLabel>
              <FieldLabel label="Notas Administrativas:">
                <Input value={cli.notas} onChange={(e) => setCliField("notas", e.target.value)} className="h-10" />
              </FieldLabel>
              <AuthorizedContactsEditor value={authContacts} onChange={setAuthContacts} />
            </div>
            )}
            </fieldset>
            )}
          </TabsContent>

          {/* PERMISSÕES — lista única por USUÁRIO (GPS Drive Backend,
              decisão 2026-08-26, revertida no mesmo dia: nunca mais matriz
              por papel, ver gpsdrive-user-permissions.service.ts) — só
              Administrator edita, o backend rejeita com 403 pra qualquer
              outro papel. Os antigos 4 toggles soltos no topo
              (Administrador/Somente leitura/Somente leitura de
              dispositivos/Usuário desabilitado, campos brutos do Traccar)
              foram removidos em 2026-08-27: "Administrador" agora é uma
              categoria dentro da própria lista (UserPermissionsAccordion) e
              implica acesso total ao sistema — ver requirePermission no
              backend e handleToggleCategory acima, que mantém o campo
              `administrator` do Traccar em sincronia. `somenteLeitura`/
              `somenteLeituraDispositivos`/`desabilitado` continuam sendo
              lidos e salvos como antes (useEffect de carga + handleSave),
              só não têm mais controle nesta tela — "Usuário desabilitado"
              já tem toggle próprio na lista de usuários (onToggleDisabled),
              os outros dois hoje só editáveis direto no Traccar. */}
          <TabsContent value="permissoes" className="px-6 py-5">
            {identityFailed ? (
              <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                Não foi possível carregar as permissões.{" "}
                <button type="button" className="underline" onClick={() => refetchIdentity()}>
                  Tentar de novo
                </button>
              </div>
            ) : persisted && (!identity || userPermissionsLoading) ? (
              <div className="mt-6 text-xs text-muted-foreground">Carregando permissões…</div>
            ) : (
              <>
                {/* Grupo mudou de aba (pedido 2026-09-03: "não quero que o
                    grupo fique dentro de permissões") — select/criar/editar/
                    excluir agora vivem na aba PRINCIPAL, em cima do Nome. Só
                    o resultado (accordion) continua aqui. */}
                {!persisted && pendingRoleId && (
                  <div className="mt-6 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300">
                    As permissões deste usuário vêm do Grupo "{gpsDriveRoles.find((r) => r.id === pendingRoleId)?.name}"
                    escolhido na aba PRINCIPAL — serão aplicadas ao salvar.
                  </div>
                )}
                {!persisted && !pendingRoleId && canManagePermissions && (
                  <div className="mb-3 mt-6 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                    Marque as permissões abaixo — elas são aplicadas junto com a criação do usuário. Ou escolha um
                    Grupo pronto na aba PRINCIPAL.
                  </div>
                )}
                {!canManagePermissions && (
                  <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800 first:mt-6 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-300">
                    Só Administrator pode alterar permissões — você pode visualizar, mas os controles abaixo estão travados.
                  </div>
                )}

                {(persisted || !pendingRoleId) && (
                  <>
                    <div className="mb-2 text-xs font-medium text-muted-foreground first:mt-6">Permissões</div>
                    <UserPermissionsAccordion
                      values={editablePermissions}
                      onToggleCategory={handleToggleCategory}
                      onToggleAction={handleToggleAction}
                      disabled={!canManagePermissions}
                      categories={userPermissionCategories}
                    />
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* VEÍCULOS — vínculo real via POST/DELETE /api/permissions */}
          <TabsContent value="veiculos" className="px-6 py-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">Vincular veículos ao usuário</div>
                <div className="text-xs text-muted-foreground">
                  Selecione os veículos que este usuário poderá acessar.
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                <Car className="h-3.5 w-3.5" />
                {vincCount} selecionado{vincCount === 1 ? "" : "s"}
              </span>
            </div>

            {persisted && loadingDeviceLinks ? (
              <div className="p-8 text-center text-xs text-muted-foreground">
                Carregando veículos vinculados…
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllFiltered}>
                    Selecionar todos
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSelection}>
                    Limpar
                  </Button>
                </div>

                <PermissionLinkPicker
                  items={filteredDevices}
                  getId={(v) => Number(v.id)}
                  isSelected={(id) => !!vincSelected[id]}
                  onToggle={(id) => toggleVinc(String(id))}
                  query={vincQuery}
                  onQueryChange={setVincQuery}
                  searchPlaceholder="Buscar por nome, placa ou motorista..."
                  emptyText="Nenhum veículo encontrado."
                  renderRow={(v, checked) => {
                    const plate = typeof v.attributes?.plate === "string" ? v.attributes.plate : v.uniqueId;
                    const driver = typeof v.attributes?.driver === "string" ? v.attributes.driver : undefined;
                    const fetchingThis = anchorPositionLoading && anchorTargetDevice?.id === v.id;
                    return (
                      <>
                        <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted">
                          <Car className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate text-sm font-medium">{v.name}</span>
                            {assistanceByDevice?.[String(v.id)] && (
                              <AssistancePlanBadge
                                plan={assistanceByDevice[String(v.id)]!.planName}
                                group={assistanceByDevice[String(v.id)]!.groupName}
                              />
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                            {plate && <span className="font-mono uppercase">{plate}</span>}
                            {driver && (
                              <>
                                {plate && <span>•</span>}
                                <span>{driver}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Âncora (pedido 2026-09-04) — só faz sentido pra um
                            veículo já vinculado; type="button" +
                            preventDefault/stopPropagation pra não disparar o
                            toggle do checkbox da linha (ver <label> em
                            PermissionLinkPicker). */}
                        {checked && canAnchor && (
                          <button
                            type="button"
                            disabled={fetchingThis}
                            title="Configurar âncora"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setAnchorTargetDevice(v);
                            }}
                            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-sky-600 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {fetchingThis ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Anchor className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </>
                    );
                  }}
                />
                {anchorTargetDevice && anchorPosition && (
                  <VehicleAnchorDialog
                    vehicle={{
                      id: anchorTargetDevice.id,
                      name: anchorTargetDevice.name,
                      plate:
                        typeof anchorTargetDevice.attributes?.plate === "string"
                          ? anchorTargetDevice.attributes.plate
                          : (anchorTargetDevice.uniqueId ?? ""),
                      lat: anchorPosition.lat,
                      lng: anchorPosition.lng,
                      hasPosition: anchorPosition.lat !== 0 || anchorPosition.lng !== 0,
                      offline: anchorPosition.offline,
                    }}
                    open
                    onClose={() => setAnchorTargetDevice(null)}
                  />
                )}
              </>
            )}
          </TabsContent>

          {/* APP — ainda não implementado nesta etapa */}
          {/* FATURAMENTO — cobranças reais do cliente no Asaas (ver
              gpsdrive-billing.service.ts). Recorrência/Carnê ainda não têm
              backend (só "Cobrança Avulsa" cria de verdade) — nada de
              botão fake que não faz nada, por isso não aparecem aqui
              ainda. */}
          {persisted && (
            <TabsContent value="faturamento" className="px-6 py-5">
              {!canViewFaturamento ? (
                <p className="mt-6 text-sm text-muted-foreground">Você não tem permissão para ver esta aba.</p>
              ) : !identity ? (
                <div className="text-xs text-muted-foreground">Carregando faturamento…</div>
              ) : invoicesPage?.asaasCustomerId === null ? (
                <div
                  className={
                    invoicesPage.asaasSyncError
                      ? "rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive"
                      : "rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground"
                  }
                >
                  {invoicesPage.asaasSyncError ? (
                    <>
                      A última tentativa de sincronizar este cliente com o Asaas falhou:{" "}
                      <span className="font-medium">{invoicesPage.asaasSyncError}</span>. Corrija o dado
                      (geralmente o e-mail, na aba PRINCIPAL ou em "Email de Cobrança" na aba CLIENTE) e
                      salve a aba CLIENTE de novo pra tentar sincronizar mais uma vez.
                    </>
                  ) : (
                    <>
                      Este cliente ainda não foi sincronizado com o Asaas. Salve a aba CLIENTE (com a
                      integração ativa em Integrações) para começar a faturar.
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {invoicesPage ? `${invoicesPage.totalCount} cobrança(s) no Asaas` : ""}
                    </div>
                    {canCreateFaturamento && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setShowRecurrenceDialog(true)}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Recorrência
                        </Button>
                        <Button size="sm" onClick={() => setShowNewInvoiceForm((v) => !v)}>
                          <Plus className="mr-1 h-3.5 w-3.5" /> Cobrança Avulsa
                        </Button>
                      </div>
                    )}
                  </div>

                  {showNewInvoiceForm && (
                    <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-border bg-muted/20 p-3 md:grid-cols-4">
                      <FieldLabel label="Valor:" required>
                        <Input
                          placeholder="R$ 0,00"
                          value={newInvoice.value}
                          onChange={(e) => setNewInvoice((s) => ({ ...s, value: e.target.value }))}
                          className="h-10"
                        />
                      </FieldLabel>
                      <FieldLabel label="Vencimento:" required>
                        <Input
                          type="date"
                          value={newInvoice.dueDate}
                          onChange={(e) => setNewInvoice((s) => ({ ...s, dueDate: e.target.value }))}
                          className="h-10"
                        />
                      </FieldLabel>
                      <FieldLabel label="Forma de pagamento:">
                        <Select
                          value={newInvoice.billingType}
                          onValueChange={(v) => setNewInvoice((s) => ({ ...s, billingType: v as InvoiceBillingType }))}
                        >
                          <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PIX">PIX</SelectItem>
                            <SelectItem value="BOLETO">Boleto</SelectItem>
                            <SelectItem value="CREDIT_CARD">Cartão de crédito</SelectItem>
                            <SelectItem value="UNDEFINED">Cliente escolhe</SelectItem>
                          </SelectContent>
                        </Select>
                        {newInvoice.billingType !== "PIX" && (
                          <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                            Só PIX mostra o QR code direto no app do cliente — as outras formas abrem um link externo do Asaas.
                          </p>
                        )}
                      </FieldLabel>
                      <FieldLabel label="Descrição (opcional):">
                        <Input
                          value={newInvoice.description}
                          onChange={(e) => setNewInvoice((s) => ({ ...s, description: e.target.value }))}
                          className="h-10"
                        />
                      </FieldLabel>
                      <div className="flex items-center gap-2 md:col-span-4">
                        <Button size="sm" onClick={handleCreateInvoice} disabled={createInvoice.isPending}>
                          {createInvoice.isPending ? "Criando…" : "Criar cobrança"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowNewInvoiceForm(false)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Painel inline, NUNCA um <Dialog> aninhado dentro deste dialog
                      (Editar Usuário) — mesmo motivo do Criar grupo (ver linha
                      ~1893): dois Dialog do Radix empilhados quebram a
                      interação e deixam o fundo preto/opaco. */}
                  {showRecurrenceDialog && (
                    <div className="mb-4 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
                      <div className="mb-3">
                        <div className="text-sm font-semibold">Nova Recorrência</div>
                        <div className="text-xs text-muted-foreground">
                          Uma única assinatura no Asaas, com o valor somado dos veículos marcados —
                          cada veículo pode estar num plano diferente.
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="mb-2 text-xs font-semibold text-muted-foreground">
                            Veículos e planos
                          </div>
                          {!linkedDevices?.length ? (
                            <div className="text-xs text-muted-foreground">
                              Nenhum veículo vinculado a este usuário ainda (aba VEÍCULOS).
                            </div>
                          ) : (
                            <div className="space-y-2 rounded-md border border-border p-2">
                              {linkedDevices.map((d) => {
                                const id = String(d.id);
                                const plan = vehiclePlans[id] ?? { selected: false, planId: "", valor: "" };
                                const plate = typeof d.attributes?.plate === "string" ? d.attributes.plate : undefined;
                                const label = plate ? `${d.name} - ${plate}` : d.name;
                                return (
                                  <div key={id} className="flex flex-wrap items-center gap-2 text-xs">
                                    <label className="flex min-w-[140px] flex-1 items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={plan.selected}
                                        onChange={(e) =>
                                          setVehiclePlans((s) => ({
                                            ...s,
                                            [id]: { ...plan, selected: e.target.checked },
                                          }))
                                        }
                                      />
                                      {label}
                                    </label>
                                    <Select value={plan.planId} onValueChange={(v) => selectVehiclePlan(id, v)}>
                                      <SelectTrigger className="h-8 w-[170px]">
                                        <SelectValue placeholder="Selecione o plano" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {!plans?.length ? (
                                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                            Nenhum plano cadastrado
                                          </div>
                                        ) : (
                                          plans.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                              {p.serviceGroupId ? "🛡 " : ""}
                                              {p.name} — {planCurrencyFormatter.format(p.value)}
                                            </SelectItem>
                                          ))
                                        )}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      placeholder="R$ 0,00"
                                      value={plan.valor}
                                      onChange={(e) => setVehiclePlans((s) => ({ ...s, [id]: { ...plan, valor: e.target.value } }))}
                                      className="h-8 w-[110px]"
                                    />
                                    {/* Plano "com assistência" (Planos) → o veículo passa a ter
                                        assistência automaticamente ao criar a recorrência. */}
                                    {plans?.find((p) => p.id === plan.planId)?.serviceGroupId && <AssistancePlanBadge plan="Com assistência" />}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {selectedVehiclePlans.length > 0 && (
                            <div className="mt-2 text-right text-xs font-semibold">
                              Total: R$ {recurrenceTotalValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <FieldLabel label="Periodicidade da cobrança:">
                            <Select
                              value={recurrence.cycle}
                              onValueChange={(v) => setRecurrence((s) => ({ ...s, cycle: v as SubscriptionCycle }))}
                            >
                              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(Object.keys(CYCLE_LABEL) as SubscriptionCycle[]).map((c) => (
                                  <SelectItem key={c} value={c}>{CYCLE_LABEL[c]}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FieldLabel>
                          <FieldLabel label="Forma de pagamento:">
                            <Select
                              value={recurrence.billingType}
                              onValueChange={(v) => setRecurrence((s) => ({ ...s, billingType: v as InvoiceBillingType }))}
                            >
                              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PIX">PIX</SelectItem>
                                <SelectItem value="BOLETO">Boleto</SelectItem>
                                <SelectItem value="CREDIT_CARD">Cartão de crédito</SelectItem>
                                <SelectItem value="UNDEFINED">Cliente escolhe</SelectItem>
                              </SelectContent>
                            </Select>
                            {recurrence.billingType !== "PIX" && (
                              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                                Só PIX mostra o QR code direto no app do cliente — as outras formas abrem um link externo do Asaas, em toda fatura gerada por essa recorrência.
                              </p>
                            )}
                          </FieldLabel>
                          <FieldLabel label="Data de Vencimento:" required>
                            <Input
                              type="date"
                              value={recurrence.nextDueDate}
                              onChange={(e) => setRecurrence((s) => ({ ...s, nextDueDate: e.target.value }))}
                              className="h-10"
                            />
                            {cli.diaVencimento && (
                              <p className="mt-1 text-[11px] text-muted-foreground">
                                Dia pré-preenchido com o "Dia de vencimento (Fatura)" da aba Cliente — ajuste o
                                mês/ano da primeira cobrança à vontade.
                              </p>
                            )}
                          </FieldLabel>
                        </div>

                        <div>
                          <div className="mb-2 text-xs font-semibold text-muted-foreground">Juros e multa</div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <FieldLabel label="Juros para pagamento após o vencimento (% ao mês):">
                              <Input
                                placeholder="0"
                                value={recurrence.interestValue}
                                onChange={(e) => setRecurrence((s) => ({ ...s, interestValue: e.target.value }))}
                                className="h-10"
                              />
                            </FieldLabel>
                            <FieldLabel label="Tipo de multa:">
                              <Select
                                value={recurrence.fineType}
                                onValueChange={(v) => setRecurrence((s) => ({ ...s, fineType: v as "FIXED" | "PERCENTAGE" }))}
                              >
                                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PERCENTAGE">Percentual</SelectItem>
                                  <SelectItem value="FIXED">Valor fixo</SelectItem>
                                </SelectContent>
                              </Select>
                            </FieldLabel>
                            <FieldLabel label="Multa para pagamento após o vencimento:">
                              <Input
                                placeholder="0"
                                value={recurrence.fineValue}
                                onChange={(e) => setRecurrence((s) => ({ ...s, fineValue: e.target.value }))}
                                className="h-10"
                              />
                            </FieldLabel>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2 text-xs font-semibold text-muted-foreground">
                            Configurações de Desconto
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <FieldLabel label="Dias para desconto:">
                              <Input
                                placeholder="0"
                                value={recurrence.discountDays}
                                onChange={(e) => setRecurrence((s) => ({ ...s, discountDays: e.target.value }))}
                                className="h-10"
                              />
                            </FieldLabel>
                            <FieldLabel label="Tipo de desconto:">
                              <Select
                                value={recurrence.discountType}
                                onValueChange={(v) => setRecurrence((s) => ({ ...s, discountType: v as "FIXED" | "PERCENTAGE" }))}
                              >
                                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PERCENTAGE">Percentual</SelectItem>
                                  <SelectItem value="FIXED">Valor fixo</SelectItem>
                                </SelectContent>
                              </Select>
                            </FieldLabel>
                            <FieldLabel label="Valor desconto:">
                              <Input
                                placeholder="0"
                                value={recurrence.discountValue}
                                onChange={(e) => setRecurrence((s) => ({ ...s, discountValue: e.target.value }))}
                                className="h-10"
                              />
                            </FieldLabel>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                        <Button variant="outline" onClick={() => setShowRecurrenceDialog(false)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleCreateSubscription} disabled={createSubscription.isPending}>
                          {createSubscription.isPending ? "Criando…" : "Criar recorrência"}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="overflow-hidden rounded-lg border border-border">
                    <div className="grid grid-cols-[110px_1fr_120px_110px_100px_160px] items-center gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <div>Status</div>
                      <div>Descrição</div>
                      <div>Forma de pagamento</div>
                      <div>Vencimento</div>
                      <div className="text-right">Valor</div>
                      <div className="text-right">Ações</div>
                    </div>
                    <div className="divide-y divide-border">
                      {loadingInvoices ? (
                        <div className="px-3 py-6 text-center text-xs text-muted-foreground">Carregando…</div>
                      ) : !invoicesPage?.invoices.length ? (
                        <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                          Nenhuma cobrança ainda.
                        </div>
                      ) : (
                        invoicesPage.invoices.map((inv) => {
                          const editable = inv.status === "pendente" || inv.status === "vencida";
                          return (
                            <div
                              key={inv.id}
                              className={cn(
                                "grid grid-cols-[110px_1fr_120px_110px_100px_160px] items-center gap-2 px-3 py-2 text-xs",
                                INVOICE_STATUS_STYLE[inv.status].row,
                              )}
                            >
                              <span
                                className={cn(
                                  "inline-flex w-fit rounded px-2 py-0.5 text-[10px] font-semibold",
                                  INVOICE_STATUS_STYLE[inv.status].badge,
                                )}
                              >
                                {INVOICE_STATUS_STYLE[inv.status].label}
                              </span>
                              <div>
                                <div>{inv.description || "Cobrança"}</div>
                                <span
                                  className={cn(
                                    "mt-0.5 inline-flex w-fit rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                                    INVOICE_ORIGIN_LABEL[inv.origin].badge,
                                  )}
                                >
                                  {INVOICE_ORIGIN_LABEL[inv.origin].label}
                                </span>
                              </div>
                              <div>{BILLING_TYPE_LABEL[inv.billingType] ?? inv.billingType}</div>
                              <div>{new Date(`${inv.dueDate}T00:00:00`).toLocaleDateString("pt-BR")}</div>
                              <div className="text-right font-medium">
                                {inv.value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                              </div>
                              <div className="flex items-center justify-end gap-1.5 text-muted-foreground">
                                {inv.invoiceUrl && (
                                  <button
                                    type="button"
                                    title="Ver no Asaas"
                                    onClick={() => window.open(inv.invoiceUrl!, "_blank", "noopener,noreferrer")}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                {(inv.bankSlipUrl || inv.invoiceUrl) && (
                                  <button
                                    type="button"
                                    title="Baixar fatura (PDF)"
                                    onClick={() =>
                                      window.open(inv.bankSlipUrl ?? inv.invoiceUrl!, "_blank", "noopener,noreferrer")
                                    }
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  title="Enviar fatura pelo WhatsApp"
                                  onClick={() => handleSendInvoiceWhatsApp(inv)}
                                >
                                  <MessageCircle className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title={editable ? "Editar cobrança" : "Só é possível editar cobranças pendentes"}
                                  disabled={!editable || !canEditFaturamento}
                                  onClick={() => openEditInvoice(inv)}
                                  className="disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  title={editable ? "Excluir cobrança" : "Só é possível excluir cobranças pendentes"}
                                  disabled={!editable || !canDeleteFaturamento}
                                  onClick={() => handleDeleteInvoice(inv)}
                                  className="text-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:opacity-40"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  {invoicesPage && invoicesPage.totalCount > 0 && (
                    <div className="mt-3 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                      <span>
                        Exibindo {Math.min(invoicesOffset + 1, invoicesPage.totalCount)} a{" "}
                        {Math.min(invoicesOffset + invoicesPage.invoices.length, invoicesPage.totalCount)} de{" "}
                        {invoicesPage.totalCount}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={invoicesOffset === 0}
                        onClick={() => setInvoicesOffset((o) => Math.max(0, o - 12))}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!invoicesPage.hasMore}
                        onClick={() => setInvoicesOffset((o) => o + 12)}
                      >
                        Próximo
                      </Button>
                    </div>
                  )}

                  {/* Painel inline pelo mesmo motivo do bloco acima — nunca
                      <Dialog> aninhado dentro do Editar Usuário. */}
                  {!!editingInvoice && (
                    <div className="mb-4 rounded-md border border-border bg-muted/20 p-3">
                      <div className="mb-3">
                        <div className="text-sm font-semibold">Editar cobrança</div>
                        <div className="text-xs text-muted-foreground">
                          Só é possível editar valor, vencimento, forma de pagamento e descrição de
                          cobranças ainda não pagas.
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <FieldLabel label="Valor:" required>
                          <Input
                            placeholder="R$ 0,00"
                            value={editInvoiceForm.value}
                            onChange={(e) => setEditInvoiceForm((s) => ({ ...s, value: e.target.value }))}
                            className="h-10"
                          />
                        </FieldLabel>
                        <FieldLabel label="Vencimento:" required>
                          <Input
                            type="date"
                            value={editInvoiceForm.dueDate}
                            onChange={(e) => setEditInvoiceForm((s) => ({ ...s, dueDate: e.target.value }))}
                            className="h-10"
                          />
                        </FieldLabel>
                        <FieldLabel label="Forma de pagamento:">
                          <Select
                            value={editInvoiceForm.billingType}
                            onValueChange={(v) =>
                              setEditInvoiceForm((s) => ({ ...s, billingType: v as InvoiceBillingType }))
                            }
                          >
                            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PIX">PIX</SelectItem>
                              <SelectItem value="BOLETO">Boleto</SelectItem>
                              <SelectItem value="CREDIT_CARD">Cartão de crédito</SelectItem>
                              <SelectItem value="UNDEFINED">Cliente escolhe</SelectItem>
                            </SelectContent>
                          </Select>
                          {editInvoiceForm.billingType !== "PIX" && (
                            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                              Só PIX mostra o QR code direto no app do cliente — as outras formas abrem um link externo do Asaas.
                            </p>
                          )}
                        </FieldLabel>
                        <FieldLabel label="Descrição (opcional):">
                          <Input
                            value={editInvoiceForm.description}
                            onChange={(e) => setEditInvoiceForm((s) => ({ ...s, description: e.target.value }))}
                            className="h-10"
                          />
                        </FieldLabel>
                      </div>
                      <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                        <Button variant="outline" onClick={() => setEditingInvoice(null)}>
                          Cancelar
                        </Button>
                        <Button onClick={handleUpdateInvoice} disabled={updateInvoice.isPending}>
                          {updateInvoice.isPending ? "Salvando…" : "Salvar alterações"}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          )}

          {/* DOCUMENTOS — arquivos reais do cliente (contrato, RG/CNH,
              comprovante de residência/renda etc), ver
              gpsdrive-client-documents.service.ts. */}
          {persisted && (
            <TabsContent value="documentos" className="px-6 py-5">
              {!canViewDocumentos ? (
                <p className="mt-6 text-sm text-muted-foreground">Você não tem permissão para ver esta aba.</p>
              ) : (
              <>
              <div className="mb-4 flex flex-col gap-2 rounded-lg border-2 border-dashed border-sky-300 bg-sky-50/40 p-4 dark:border-sky-900 dark:bg-sky-950/20 sm:flex-row sm:items-center sm:justify-between">
                <FieldLabel label="Tipo de documento:">
                  <Select
                    value={newDocumentCategory}
                    onValueChange={(v) => setNewDocumentCategory(v as ClientDocumentCategory)}
                  >
                    <SelectTrigger className="h-10 sm:w-64"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(DOCUMENT_CATEGORY_LABEL) as ClientDocumentCategory[]).map((c) => (
                        <SelectItem key={c} value={c}>{DOCUMENT_CATEGORY_LABEL[c]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldLabel>
                <input
                  ref={documentFileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx"
                  onChange={handleDocumentFilePicked}
                />
                <Button
                  className="bg-slate-500 hover:bg-slate-600 sm:self-end"
                  disabled={!identity || !canCreateDocumentos || uploadDocument.isPending}
                  onClick={() => documentFileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploadDocument.isPending ? "Enviando…" : "Enviar documento"}
                </Button>
              </div>

              <div className="overflow-hidden rounded-lg border border-border">
                <div className="grid grid-cols-[1fr_170px_90px_100px_70px] items-center gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <div>Arquivo</div>
                  <div>Tipo</div>
                  <div className="text-right">Tamanho</div>
                  <div>Enviado em</div>
                  <div className="text-right">Ações</div>
                </div>
                <div className="divide-y divide-border">
                  {loadingDocuments ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">Carregando…</div>
                  ) : !clientDocuments?.length ? (
                    <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                      Nenhum documento ainda.
                    </div>
                  ) : (
                    clientDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="grid grid-cols-[1fr_170px_90px_100px_70px] items-center gap-2 px-3 py-2 text-xs"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate" title={doc.fileName}>{doc.fileName}</span>
                        </div>
                        <div className="text-muted-foreground">{DOCUMENT_CATEGORY_LABEL[doc.category]}</div>
                        <div className="text-right text-muted-foreground">{formatFileSize(doc.sizeBytes)}</div>
                        <div className="text-muted-foreground">
                          {new Date(doc.createdAt).toLocaleDateString("pt-BR")}
                        </div>
                        <div className="flex items-center justify-end gap-1.5 text-muted-foreground">
                          <button
                            type="button"
                            title="Baixar"
                            onClick={() => window.open(resolveGpsDriveUrl(doc.url), "_blank", "noopener,noreferrer")}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                          {canDeleteDocumentos && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => handleDeleteDocument(doc)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              </>
              )}
            </TabsContent>
          )}

          {/* CONTRATO — modelo, signatário e valores reais deste cliente
              (ver contractForm/handleSaveClientContract acima). Modelos e
              signatários vêm de Administração > Conta & Integrações >
              Contrato (admin.contract.tsx). Assinatura por autoatendimento
              (link sem login, ver handleGenerateSigningLink acima e
              contrato.assinar.$token.tsx) desde 2026-09-11 — "Marcar como
              assinado" continua disponível como caminho manual (assinado em
              papel/por fora). */}
          {persisted && (
            <TabsContent value="contrato" className="px-6 py-5">
              {!canViewContrato ? (
                <p className="mt-6 text-sm text-muted-foreground">Você não tem permissão para ver esta aba.</p>
              ) : (() => {
                const money = (v: number | string | null | undefined) =>
                  v != null && v !== "" ? planCurrencyFormatter.format(Number(v)) : "—";
                const fmtDate = (iso: string | null | undefined) =>
                  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
                const current = clientContract?.templateId ? clientContract : null;
                const pending = !!current && !current.signedAt;
                const rows = [
                  ...(current
                    ? [{
                        key: "current",
                        version: current.version ?? 1,
                        title: contractTemplates?.find((t) => t.id === current.templateId)?.title ?? "Contrato",
                        contract: current,
                        status: current.signedAt
                          ? ({ label: "Assinado", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", sub: fmtDate(current.signedAt) })
                          : current.signingToken
                            ? ({ label: "Aguardando assinatura", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", sub: "link enviado, cliente ainda não assinou" })
                            : ({ label: "Pendente", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300", sub: "link ainda não enviado" }),
                      }]
                    : []),
                  ...contractVersions.map((v) => ({
                    key: v.id,
                    version: v.version,
                    title: v.template?.title ?? "Contrato",
                    contract: v.contract,
                    status: { label: "Assinado (anterior)", cls: "bg-emerald-500/10 text-emerald-700/80 dark:text-emerald-400/80", sub: fmtDate(v.signedAt) },
                  })),
                ];
                return (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">Contratos do cliente</p>
                        <p className="text-xs text-muted-foreground">
                          Crie o contrato, mande o link para o cliente assinar e acompanhe aqui.
                        </p>
                      </div>
                      {canEditContrato && (
                        <Button
                          onClick={() => setContractFormMode("new")}
                          disabled={pending}
                          title={pending ? "Já existe um contrato aguardando assinatura — edite ou exclua ele" : undefined}
                        >
                          <Plus className="mr-2 h-4 w-4" /> Criar contrato
                        </Button>
                      )}
                    </div>

                    {rows.length === 0 ? (
                      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
                        <IdCard className="h-8 w-8 text-muted-foreground" />
                        <p className="text-sm font-medium">Nenhum contrato ainda</p>
                        <p className="text-xs text-muted-foreground">Clique em “Criar contrato” para preencher.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border rounded-lg border border-border">
                        {rows.map((r) => {
                          const isCurrent = r.key === "current";
                          const vehicles = r.contract.vehicles?.length ?? 0;
                          return (
                            <div key={r.key} className="space-y-2 p-3">
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-medium">{r.title}</span>
                                    <span className="text-xs text-muted-foreground">versão {r.version}</span>
                                  </div>
                                  <div className="mt-0.5 text-xs text-muted-foreground">
                                    Valor {money(r.contract.contractValue)} · Recorrência {money(r.contract.recurrenceValue)}
                                    {vehicles > 0 ? ` · ${vehicles} ${vehicles === 1 ? "veículo" : "veículos"}` : ""}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", r.status.cls)}>{r.status.label}</span>
                                  {r.status.sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{r.status.sub}</div>}
                                </div>
                                <div className="flex flex-wrap items-center gap-1">
                                  <Button size="sm" variant="outline" onClick={() => setViewingContract({ versionId: isCurrent ? null : r.key })}>
                                    <Eye className="mr-1.5 h-3.5 w-3.5" /> Ver
                                  </Button>
                                  {isCurrent && pending && canEditContrato && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => setContractFormMode("edit")}>
                                        <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => setSignPanelOpen((o) => !o)}>
                                        <Link2 className="mr-1.5 h-3.5 w-3.5" /> Enviar para assinar
                                      </Button>
                                    </>
                                  )}
                                  {canDeleteContract && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-muted-foreground hover:text-destructive"
                                      title="Excluir contrato"
                                      onClick={() => setConfirmDeleteContract(r.key)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {confirmDeleteContract === r.key && (
                                <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
                                  <span className="text-destructive">
                                    {r.contract.signedAt
                                      ? "Este contrato está ASSINADO. Excluir mesmo assim? Não dá para desfazer."
                                      : "Excluir este contrato?"}
                                    {isCurrent && contractVersions.length > 0 ? " O assinado anterior volta a valer." : ""}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={handleDeleteClientContract}
                                    disabled={deleteClientContract.isPending || deleteContractVersion.isPending}
                                  >
                                    {deleteClientContract.isPending || deleteContractVersion.isPending ? "Excluindo…" : "Excluir"}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteContract(null)}>
                                    Cancelar
                                  </Button>
                                </div>
                              )}

                              {/* Link de assinatura por autoatendimento (pedido 2026-09-11) —
                                  o cliente abre sem login, confirma dados/localização/foto e
                                  assina (ver contrato.assinar.$token.tsx). */}
                              {isCurrent && pending && signPanelOpen && canEditContrato && (
                                <div className="space-y-2 rounded-md bg-muted/40 p-3">
                                  <p className="text-xs text-muted-foreground">
                                    O cliente abre o link pelo celular, sem login, confere os dados, tira uma foto e assina.
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={handleGenerateSigningLink} disabled={generateSigningLink.isPending}>
                                      <Link2 className="mr-1.5 h-3.5 w-3.5" />
                                      {generateSigningLink.isPending ? "Gerando…" : signingLinkUrl ? "Gerar novo link" : "Gerar link de assinatura"}
                                    </Button>
                                    {signingLinkUrl && (
                                      <>
                                        <Button size="sm" variant="outline" onClick={handleCopySigningLink}>
                                          <Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar link
                                        </Button>
                                        {whatsappIntegration?.connected && (
                                          <Button
                                            size="sm"
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                            onClick={handleSendSigningLinkWhatsapp}
                                            disabled={sendSigningLinkByWhatsapp.isPending || !cli.celular}
                                            title={!cli.celular ? "Cadastre o celular do cliente (aba Cliente) antes" : undefined}
                                          >
                                            <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                                            {sendSigningLinkByWhatsapp.isPending ? "Enviando…" : "Enviar por WhatsApp"}
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  {signingLinkUrl && <p className="break-all text-xs text-muted-foreground">{signingLinkUrl}</p>}
                                </div>
                              )}

                              {isCurrent && current?.signedMethod === "link" && current.signedAt && (
                                <div className="flex flex-wrap items-start gap-3 rounded-md bg-emerald-600/5 p-3 text-xs text-muted-foreground">
                                  {current.signedPhotoUrl && (
                                    <img
                                      src={resolveGpsDriveUrl(current.signedPhotoUrl)}
                                      alt="Foto tirada pelo cliente na assinatura"
                                      className="h-20 w-16 rounded border border-border object-cover"
                                    />
                                  )}
                                  <div className="grid gap-0.5">
                                    {current.signedByName && <span>Assinado por: {current.signedByName}</span>}
                                    {current.signedLatitude != null && current.signedLongitude != null && (
                                      <a
                                        href={`https://www.google.com/maps?q=${current.signedLatitude},${current.signedLongitude}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline"
                                      >
                                        Local da assinatura no mapa
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Dialog open={!!contractFormMode} onOpenChange={(o) => !o && setContractFormMode(null)}>
                      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <IdCard className="h-4 w-4" />
                            {contractFormMode === "edit" ? "Editar contrato" : "Criar contrato"}
                          </DialogTitle>
                          <DialogDescription>
                            {contractFormMode === "new" && contractSigned
                              ? "O contrato assinado fica guardado na lista e este vira uma versão nova para o cliente assinar."
                              : "Preencha e salve. Depois é só enviar o link para o cliente assinar."}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                          <FieldLabel label="Modelo de contrato:" required>
                            <Select
                              value={contractForm.templateId}
                              onValueChange={(v) => setContractForm((st) => ({ ...st, templateId: v }))}
                            >
                              <SelectTrigger className="h-10"><SelectValue placeholder="Selecione um contrato" /></SelectTrigger>
                              <SelectContent>
                                {!contractTemplates?.length ? (
                                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    Nenhum modelo cadastrado (Administração &gt; Conta &amp; Integrações &gt; Contrato)
                                  </div>
                                ) : (
                                  contractTemplates.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </FieldLabel>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <FieldLabel label="Valor do Contrato:">
                              <Input
                                placeholder="R$ 0,00"
                                className="h-10"
                                value={contractForm.contractValue}
                                onChange={(e) => setContractForm((st) => ({ ...st, contractValue: e.target.value }))}
                              />
                            </FieldLabel>
                            <FieldLabel label="Valor da Recorrência:">
                              <Input
                                placeholder="R$ 0,00"
                                className="h-10"
                                value={contractForm.recurrenceValue}
                                onChange={(e) => setContractForm((st) => ({ ...st, recurrenceValue: e.target.value }))}
                              />
                            </FieldLabel>
                          </div>
                          <FieldLabel label="Signatários (pode marcar mais de um):">
                            <ContractSignatoryPicker
                              signatories={contractSignatories ?? []}
                              value={contractForm.signatoryIds}
                              onChange={(ids) => setContractForm((st) => ({ ...st, signatoryIds: ids }))}
                            />
                          </FieldLabel>
                          <FieldLabel label="Veículos do contrato:">
                            <ContractVehiclesEditor
                              value={contractForm.vehicles}
                              onChange={(vehicles) => setContractForm((st) => ({ ...st, vehicles }))}
                              registered={linkedDevices ?? []}
                              plans={plans}
                            />
                          </FieldLabel>
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setContractFormMode(null)}>
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleSaveClientContract}
                            disabled={updateClientContract.isPending || startNewContractVersion.isPending}
                          >
                            {updateClientContract.isPending || startNewContractVersion.isPending ? "Salvando…" : "Salvar contrato"}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <ContractPreviewDialog
                      client={viewingContract ? initial : null}
                      initialVersionId={viewingContract?.versionId ?? null}
                      onOpenChange={(o) => !o && setViewingContract(null)}
                    />
                  </div>
                );
              })()}
            </TabsContent>
          )}

          {/* LOGS — histórico real (ActivityLog + PushSubscription +
              AlertEvent, mesclado no backend, ver activity-log.service.ts).
              identity.id é o mesmo userId (UUID interno) usado pelas abas
              PERMISSÕES/CLIENTE/CONTRATO acima. */}
          {persisted && (
            <TabsContent value="logs" className="px-6 py-5">
              <UserActivityLogTab userId={identity?.id} />
            </TabsContent>
          )}

          {/* DISPOSITIVOS CONECTADOS */}
          {persisted && (
            <TabsContent value="dispositivos" className="px-6 py-5">
              <UserConnectedDevicesTab userId={identity?.id} />
            </TabsContent>
          )}

          {/* USUÁRIOS (pedido 2026-09-10/11) — quem este Cliente já criou
              sozinho pela aba "Criar usuário" do Perfil (PWA). O
              administrador com "Usuários: editar/excluir" pode editar
              (nome/telefone/senha/veículos/permissões) ou excluir daqui
              mesmo — nunca aparecem na lista PRINCIPAL de Usuários (ver
              subUserTraccarIdSet em UsersPage). */}
          {persisted && accountType === "usuario" && (
            <TabsContent value="usuarios" className="px-6 py-5">
              <div className="mb-3">
                <div className="text-sm font-semibold">Usuários criados por este cliente</div>
                <div className="text-xs text-muted-foreground">
                  Contas que este cliente criou (aba "Criar usuário" do app) pra parentes ou
                  outras pessoas acessarem o sistema.
                </div>
              </div>
              {loadingSubUsers ? (
                <div className="p-8 text-center text-xs text-muted-foreground">Carregando…</div>
              ) : subUsers.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
                  Este cliente ainda não criou nenhum usuário.
                </div>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {subUsers.map((u) => (
                    <li key={u.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{u.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {u.email}
                          {u.phone ? ` · ${u.phone}` : ""}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="mr-2 text-xs text-muted-foreground">
                          {new Date(u.createdAt).toLocaleDateString("pt-BR")}
                        </span>
                        <button
                          type="button"
                          title="Dispositivos conectados (sessões e tokens de push)"
                          onClick={() => setSubUserDetailsId(u.id)}
                          className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Smartphone className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title={canEditSubUsers ? "Editar" : "Sem permissão"}
                          disabled={!canEditSubUsers}
                          onClick={() => setEditingSubUserId(u.id)}
                          className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-sky-50 hover:text-sky-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title={canDeleteSubUsers ? "Excluir" : "Sem permissão"}
                          disabled={!canDeleteSubUsers || deletingSubUserId === u.id}
                          onClick={() => handleDeleteSubUser(u.id, u.name)}
                          className="grid h-8 w-8 place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          {deletingSubUserId === u.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          )}
        </Tabs>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-6 py-3">
          <Button variant="destructive" onClick={() => { reset(); onOpenChange(false); }}>
            Cancelar
          </Button>
          {/* Um único botão Salvar pro dialog inteiro. Usuário ainda não
              persistido: SEMPRE handleSave, não importa a aba ativa — é a
              única ação que existe ainda (criar o usuário já aplicando o
              que foi preenchido em Permissões/Cliente/Veículos, ver
              handleSave). Usuário já existente: handleSaveEdit grava TODAS
              as abas alteradas, não só a aberta. */}
          <Button
            onClick={!persisted ? handleSave : handleSaveEdit}
            disabled={
              !persisted
                ? saving || (tab === "permissoes" && !canManagePermissions)
                : saving || savingEdit || loadingUser || loadingDeviceLinks
            }
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            {saving || savingEdit ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={deleteGroupConfirmOpen} onOpenChange={setDeleteGroupConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Excluir grupo "{gpsDriveRoles.find((r) => r.id === pendingRoleId)?.name}"?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Esta ação não pode ser desfeita. Só é possível excluir um Grupo que nenhum usuário está
            usando no momento — troque o Grupo desses usuários antes, se houver algum.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDeleteGroup} disabled={deleteRole.isPending}>
            {deleteRole.isPending ? "Excluindo…" : "Excluir"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {editingSubUserId && (
      <SubUserFormDialog
        mode="edit"
        subUserId={editingSubUserId}
        open={!!editingSubUserId}
        onOpenChange={(next) => {
          if (!next) setEditingSubUserId(null);
        }}
      />
    )}

    <Dialog
      open={!!subUserDetailsId}
      onOpenChange={(next) => {
        if (!next) setSubUserDetailsId(null);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-4 w-4" />
            Dispositivos conectados
          </DialogTitle>
          <DialogDescription>
            Sessões web ativas e tokens de push nativo (app Android) deste usuário — mais de
            um token é normal (cada instalação/troca de aparelho gera um novo).
          </DialogDescription>
        </DialogHeader>
        <UserConnectedDevicesTab userId={subUserDetailsId ?? undefined} />
      </DialogContent>
    </Dialog>
    </>
  );
}

function FieldLabel({
  label,
  required,
  requiredColor,
  children,
}: {
  label: string;
  required?: boolean;
  requiredColor?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className={cn("mb-1.5 block text-xs font-medium", requiredColor ? "text-red-500" : "text-muted-foreground")}>
        {label}
        {required && <span className="ml-1 text-primary">*</span>}
      </Label>
      {children}
    </div>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-[11px] text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

// Bug real 2026-09-01: a lista inteira de usuários/clientes só era gated
// por users.edit (abrir "Editar Usuário") — sem users.view, dava pra ver a
// lista inteira mesmo assim, só não editar. Mesmo gate de devices.view do
// mapa (ver routes/index.tsx#MapPageGate).
function UsersPageGate() {
  // Bug real 2026-09-01 ("quando eu acesso usuário ele aparece sem
  // permissões e depois de segundos aparece"): usePermission() sozinho não
  // distingue "ainda carregando" de "negado de verdade" — ver
  // usePermissionGateStatus.
  const status = usePermissionGateStatus("users", "view");
  if (status === "loading") {
    return (
      <GpsInlineLoader className="h-[70vh]" />
    );
  }
  if (status === "denied") {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Sem permissão</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você não tem acesso para visualizar usuários. Peça a um administrador para conceder a
            permissão em Usuários → Editar → Permissões.
          </p>
        </div>
      </div>
    );
  }
  return <UsersPage />;
}

export const Route = createFileRoute("/users")({
  component: UsersPageGate,
  head: () => ({
    meta: [
      { title: "Usuários — Wp Rastreamento" },
      {
        name: "description",
        content:
          "Gerencie os clientes/usuários da plataforma, com filtros por tipo, status, recorrência e contrato.",
      },
    ],
  }),
});

// "Contratos" (painel expandido, seta da linha) — pedido 2026-09-11: nunca
// mais abrir o dialog inteiro de Editar Usuário só pra ver/baixar o
// contrato; popup próprio, leve, que já mostra o documento pronto (mesmo
// componente que o "Baixar (PDF)" imprime, ClientContractDocument) e um
// botão de baixar. Mesmo padrão de dados de UserVehiclesDialog
// (useDevicePermissions) + os mesmos hooks que a aba CONTRATO de
// CreateUserDialog já usava (useClientContract/useClientProfile/
// useContractTemplates/useContractSignatories/usePlans) — só que aqui é
// 100% leitura, sem nenhum campo editável.
function ContractPreviewDialog({
  client,
  onOpenChange,
  initialVersionId = null,
}: {
  client: ClientRow | null;
  onOpenChange: (open: boolean) => void;
  // Abrir direto num contrato anterior (lista da aba Contrato).
  initialVersionId?: string | null;
}) {
  const { gpsDriveStatus } = useAuth();
  const gpsDriveReady = gpsDriveStatus !== "loading";
  const {
    data: identity,
    isLoading: loadingIdentity,
    isError: identityFailed,
  } = useResolveUserByTraccarId(client?.user.id, !!client && gpsDriveReady);
  const { data: currentContract, isLoading: loadingContract } = useClientContract(identity?.id);
  // Contratos assinados anteriores (2026-10-15): "Novo contrato" em Vendas
  // guarda o assinado; aqui dá pra abrir e baixar cada versão.
  const { data: versions = [] } = useClientContractVersions(identity?.id);
  const [versionId, setVersionId] = useState<string | null>(initialVersionId);
  useEffect(() => setVersionId(initialVersionId), [initialVersionId, client]);
  const viewing = versions.find((v) => v.id === versionId) ?? null;
  const clientContract = viewing ? viewing.contract : currentContract;
  const { data: clientProfile } = useClientProfile(identity?.id);
  const { data: linkedDevices } = useDevicePermissions(client?.user.id, !!client && gpsDriveReady);
  const { data: contractTemplates } = useContractTemplates();
  const { data: contractSignatories } = useContractSignatories();
  const { data: plans } = usePlans();

  const cli = clientProfileToCliState(clientProfile);
  const template = viewing
    ? (viewing.template ?? undefined)
    : contractTemplates?.find((t) => t.id === clientContract?.templateId);
  const signers = contractSignatoryIds(clientContract).flatMap((id) => {
    const s = contractSignatories?.find((x) => x.id === id);
    return s ? [{ name: s.name, role: s.role, cpfCnpj: s.cpfCnpj }] : [];
  });
  const loading = loadingIdentity || (!!identity && loadingContract);

  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="h-4 w-4" />
            Contrato
          </DialogTitle>
          <DialogDescription>
            {client ? <span className="line-clamp-1">{client.name}</span> : null}
          </DialogDescription>
        </DialogHeader>

        {identityFailed ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Não foi possível carregar o contrato deste cliente.
          </div>
        ) : loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
        ) : !clientContract?.templateId ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
            <IdCard className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm font-medium">Nenhum contrato configurado</div>
            <div className="text-xs text-muted-foreground">
              Configure pela aba CONTRATO no Editar Usuário.
            </div>
          </div>
        ) : (() => {
          const docProps = {
            clientName: client?.name ?? "",
            cpfCnpj: cli.cpfCnpj,
            phone: cli.celular,
            email: client?.user.email ?? "",
            address: [
              [cli.endereco, cli.numero].filter(Boolean).join(", "),
              cli.bairro,
              [cli.cidade, cli.estado].filter(Boolean).join("/"),
              cli.cep,
            ]
              .filter(Boolean)
              .join(" — "),
            dueDay: cli.diaVencimento,
            vehicles: linkedDevices ?? [],
            plans: plans ?? [],
            template: template ? { title: template.title, content: template.content } : null,
            contractValue: clientContract.contractValue != null ? String(clientContract.contractValue).replace(".", ",") : "",
            recurrenceValue: clientContract.recurrenceValue != null ? String(clientContract.recurrenceValue).replace(".", ",") : "",
            contract: clientContract,
            signatory: signers[0] ?? null,
            signatories: signers,
          };
          return (
            <>
              {/* Status bem explícito no topo — pedido 2026-09-11: sem
                  "Marcar como assinado" na aba CONTRATO, este popup é o
                  ÚNICO lugar onde o atendente confirma se o cliente já
                  assinou (link de autoatendimento) ou ainda não. */}
              <div
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium",
                  clientContract.signedAt
                    ? "border-emerald-600/30 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
                    : "border-amber-600/30 bg-amber-600/10 text-amber-700 dark:text-amber-400",
                )}
              >
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                {clientContract.signedAt
                  ? `Assinado em ${new Date(clientContract.signedAt).toLocaleString("pt-BR")}`
                  : "Ainda não assinado — aguardando o cliente concluir pelo link de assinatura"}
                {(clientContract.version ?? 1) > 1 || viewing ? (
                  <span className="ml-auto font-normal">versão {viewing ? viewing.version : clientContract.version}</span>
                ) : null}
              </div>
              {versions.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">Contratos:</span>
                  <button
                    type="button"
                    onClick={() => setVersionId(null)}
                    className={cn(
                      "rounded-full border px-2.5 py-1",
                      !viewing ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
                    )}
                  >
                    Atual (versão {currentContract?.version ?? versions.length + 1})
                  </button>
                  {versions.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setVersionId(v.id)}
                      className={cn(
                        "rounded-full border px-2.5 py-1",
                        viewing?.id === v.id ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
                      )}
                    >
                      Versão {v.version}
                      {v.signedAt ? ` · ${new Date(v.signedAt).toLocaleDateString("pt-BR")}` : ""}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-3 overflow-hidden rounded-lg border border-border">
                <ClientContractDocument {...docProps} className="p-4" />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Fechar
                </Button>
                <Button onClick={() => printClientContract(`Contrato — ${client?.name ?? ""}`)}>
                  <Download className="mr-2 h-4 w-4" />
                  Baixar (PDF)
                </Button>
              </div>
              <ClientContractPrintReport open {...docProps} />
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

